import React, { useState, useEffect, useRef, useMemo } from 'react';
import API_BASE_URL from './config/api';
import {
  AppBar,
  Box,
  Stack,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Popper,
  Grow,
  MenuList,
  MenuItem,
  ClickAwayListener,
  Paper,
  TextField,
  InputAdornment,
  Badge,
  Tabs,
  Tab,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Chip,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { styled, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoIcon from '@mui/icons-material/Info';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CommandPalette from './components/CommandPalette';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { IoLinkSharp, IoStarSharp, IoApps } from 'react-icons/io5';
import { FaBrain } from 'react-icons/fa';
import { IoMdSearch, IoMdClose } from 'react-icons/io';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { FaFilePdf, FaCalendarAlt, FaFileAlt, FaDownload, FaLayerGroup, FaChartBar } from 'react-icons/fa';
import { MdAnalytics } from 'react-icons/md';
import { ImNewspaper } from 'react-icons/im';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { IoMdCloudDownload, IoMdCloudUpload } from 'react-icons/io';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme, AccentColorHoverWrapper } from './context/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Fuse from 'fuse.js';
import * as XLSX from 'xlsx';
import NotificationDropdown from './components/NotificationDropdown';
import Breadcrumb from './components/Breadcrumb';
import { useProcess } from './context/ProcessContext';
import { useFavorites } from './context/FavoritesContext';
import { usePermissions } from './context/PermissionContext';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { formatDateTime } from './utils/dateUtils';

/* ————————————————— constants ————————————————— */
const FAVORITES_KEY = 'favorites_EZProcessing';
const drawerWidth = 250; // left favorites
const historyDrawerWidth = 300; // right history

/* ————————————————— styled helpers ————————————————— */
const DropdownPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  minWidth: 300,
  maxHeight: 'calc(100vh - 80px)',
  overflowY: 'auto',
  backdropFilter: 'blur(6px)',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(33,33,33,0.92)'
      : 'rgba(255,255,255,0.92)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 20px rgba(0,0,0,0.7)'
      : '0 4px 20px rgba(0,0,0,0.15)',
  border: `2px solid ${theme.palette.primary.main}`,
  '&:hover': {
    border: `2px solid ${theme.palette.primary.dark}`,
  },
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[500],
    borderRadius: 4,
  },
}));

const HistoryButton = styled(IconButton)(({ theme, active }) => ({
  borderRadius: 6,
  padding: '8px',
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    transform: 'scale(1.1)',
    transition: 'all 0.2s ease',
  },
  transition: 'all 0.2s ease',
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 14,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  backdropFilter: 'blur(10px)',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(28,29,32,0.98)'
      : 'rgba(255,255,255,0.98)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 16px 48px rgba(0,0,0,0.6)'
      : '0 16px 48px rgba(15,23,42,0.16)',
}));

const SearchResultsArea = styled(Box)(({ theme }) => ({
  maxHeight: 420,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '4px 0',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
    borderRadius: 3,
  },
}));

const Kbd = ({ children }) => (
  <Box
    component="span"
    sx={{
      px: 0.6, py: 0.1, border: '1px solid', borderColor: 'divider', borderRadius: 0.75,
      fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: 'text.secondary', lineHeight: 1.6, whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const ResultItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'selected' })(({ theme, selected }) => ({
  borderRadius: 10,
  margin: '1px 8px',
  padding: '6px 10px',
  width: 'auto',
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  backgroundColor: selected ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1) : 'transparent',
  '&:hover': {
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.14)
      : theme.palette.action.hover,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  '& .search-row-star': { opacity: 0.35, transition: 'opacity 120ms ease' },
  '&:hover .search-row-star': { opacity: 1 },
}));

const HighlightedText = styled(Typography)(({ theme }) => ({
  '& .highlight': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.32 : 0.16),
    color: 'inherit',
    fontWeight: 700,
    borderRadius: 3,
    padding: '0 2px',
  },
}));


/* ————————————————— reusable dropdown ————————————————— */
const Dropdown = ({ icon: Icon, items, navigate, favorites, toggleFavorite, isFavorite, isOpen, setIsOpen, onIconClick, iconTooltip, category }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(isOpen || false);
  const timer = useRef(null);
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);
  const openMenu = () => {
    clearTimeout(timer.current);
    setOpen(true);
    if (setIsOpen) setIsOpen(true);
  };
  const scheduleClose = () => {
    timer.current = setTimeout(() => {
      setOpen(false);
      if (setIsOpen) setIsOpen(false);
    }, 200);
  };
  const handleIconClick = (e) => {
    if (onIconClick) {
      e.stopPropagation();
      onIconClick();
    } else {
      openMenu();
    }
  };
  return (
    <Box component="span">
      <Tooltip title={iconTooltip || ''}>
        <IconButton
          ref={anchorRef}
          size="large"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
          onClick={handleIconClick}
          sx={{
            p: 1,
            color: (t) =>
              t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary,
            '&:hover': {
              bgcolor: 'primary.main',
              color: (t) => t.palette.getContrastText(t.palette.primary.main),
            },
          }}
        >
          <Icon style={{ fontSize: 24 }} />
        </IconButton>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal
        onMouseEnter={() => clearTimeout(timer.current)}
        onMouseLeave={scheduleClose}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <DropdownPaper>
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                  if (setIsOpen) setIsOpen(false);
                }}
              >
                <MenuList dense autoFocus>
                  {items.map(({ text, path }) => {
                    const isFavorited = isFavorite(path);
                    return (
                      <MenuItem
                        key={path}
                        onClick={() => {
                          navigate(path);
                          setOpen(false);
                          if (setIsOpen) setIsOpen(false);
                        }}
                        sx={{
                          fontWeight: 500,
                          fontSize: '1rem',
                          py: 1.5,
                          px: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          '&:hover, &:focus': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                          },
                        }}
                      >
                        <span>{text}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite({ text, path, category });
                          }}
                          sx={{ color: isFavorited ? 'gold' : 'grey' }}
                        >
                          {isFavorited ? '★' : '☆'}
                        </IconButton>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </DropdownPaper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

/* ————————————————— Dashboard ————————————————— */
const Dashboard = () => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  // RetailOS is a full dashboard with its own navigation; on its route the
  // Back button and breadcrumb strip stay out of the way (the AppBar remains).
  const immersive = /^\/dashboard\/retail-os(\/|$)/.test(location.pathname);
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAdmin, isOwner, hasAccessToPath, clearPermissions } = usePermissions();

  // Load user access level and ID from localStorage
  const userAccessLevel = parseInt(localStorage.getItem('userAccessLevel') || '0', 10);
  const userId = parseInt(localStorage.getItem('userId') || '4', 10); // Assume userId is stored

  /* -------- favorites (use context) -------- */
  const { favorites, setFavorites, toggleFavorite, isFavorite } = useFavorites();

  /* -------- drag and drop for rearranging favorites -------- */
  const handleDragStart = (e, index) => e.dataTransfer.setData('text/plain', index);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    const newFavorites = [...favorites];
    const [draggedItem] = newFavorites.splice(dragIndex, 1);
    newFavorites.splice(dropIndex, 0, draggedItem);
    setFavorites(newFavorites);
  };

  /* -------- HISTORY state & fetch -------- */
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historySort, setHistorySort] = useState('createdAt');
  const [isReverseSort, setIsReverseSort] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  useEffect(() => {
    if (historyOpen) {
      const fetchHist = async () => {
        try {
          setHistLoading(true);
          const token = localStorage.getItem('jwtToken');
          if (!token) {
            throw new Error('No authentication token found. Please log in again.');
          }
          const res = await fetch(`${API_BASE_URL}/api/Users/userUploadOrDownloadHistory?limit=500`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Server error (${res.status}): ${errorText || 'Unknown error'}`);
          }
          const json = await res.json();
          // Handle both array response and object with Data property
          const historyData = Array.isArray(json) ? json : (json.Data || []);
          setHistory(historyData);
        } catch (err) {
          console.error('History fetch error:', err);
          alert(`Failed to load history: ${err.message}`);
          setHistory([]);
        } finally {
          setHistLoading(false);
        }
      };
      fetchHist();
    }
  }, [historyOpen]);

  /* -------- Fuse.js Search for History -------- */
  const filteredData = useMemo(() => {
    if (historyFilter === 'upload') return history.filter((h) => h.upload === false);
    if (historyFilter === 'download') return history.filter((h) => h.upload === true);
    return history;
  }, [history, historyFilter]);

  const fuse = useMemo(() => {
    return new Fuse(filteredData, {
      keys: ['userEmailId', 'filename', 'createdAt'],
      threshold: 0.3,
      ignoreLocation: true,
      includeScore: true,
      shouldSort: true,
    });
  }, [filteredData]);

  const filteredHistory = useMemo(() => {
    let results = historySearch.trim() ? fuse.search(historySearch).map((result) => result.item) : filteredData;
    return results.sort((a, b) => {
      if (historySort === 'createdAt')
        return isReverseSort
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      if (historySort === 'interfaceName')
        return isReverseSort
          ? (a.interfaceName || '').localeCompare(b.interfaceName || '')
          : (b.interfaceName || '').localeCompare(a.interfaceName || '');
      if (historySort === 'filename')
        return isReverseSort
          ? a.filename.localeCompare(b.filename)
          : b.filename.localeCompare(a.filename);
      return 0;
    });
  }, [filteredData, historySearch, historySort, isReverseSort, fuse]);

  /* -------- Excel Export -------- */
  const handleExportExcel = async () => {
    const token = localStorage.getItem('jwtToken');
    const rows = [];

    for (const h of filteredHistory) {
      if (h.link && h.link.startsWith('batch:')) {
        const batchId = h.link.replace('batch:', '');
        try {
          const res = await fetch(`${API_BASE_URL}/api/AIImageGenerator/history/${batchId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success && data.images && data.images.length > 0) {
            data.images.forEach((img, i) => {
              rows.push({
                Email: h.userEmailId,
                Filename: img.fileName || `${h.filename} (${i + 1})`,
                Link: img.s3Url,
                Date: formatDateTime(h.createdAt),
              });
            });
            continue;
          }
        } catch (err) {
          console.error('Failed to fetch batch images for export:', err);
        }
      }
      rows.push({
        Email: h.userEmailId,
        Filename: h.filename,
        Link: h.link,
        Date: formatDateTime(h.createdAt),
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);

    // Make Link column clickable hyperlinks
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cell = ws[XLSX.utils.encode_cell({ r: row, c: 2 })];
      if (cell && cell.v && cell.v.startsWith('http')) {
        cell.l = { Target: cell.v };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'History');
    const now = new Date();
    const dt = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`;
    XLSX.writeFile(wb, `history_export_${dt}.xlsx`);
  };

  /* -------- drawer open / close -------- */
  const [drawerOpen, setDrawerOpen] = useState(() => {
    try { return localStorage.getItem('favoritesDrawerOpen') === 'true'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('favoritesDrawerOpen', drawerOpen ? 'true' : 'false'); } catch {} }, [drawerOpen]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /* -------- spinner on route change -------- */
  const [loading, setLoading] = useState(false);

  /* -------- Track recent page visits -------- */
  const RECENT_VISITS_KEY = 'recentPageVisits';
  const MAX_RECENT_VISITS = 10;

  // Map of path slugs to friendly names and types
  const pathToInterfaceMap = useMemo(() => ({
    // PDF Tools
    'ama9999-ucc-labels': { text: 'AMA9999 UCC Labels', type: 'pdf' },
    'amazon-di-ucc-label': { text: 'Amazon DIO UCC Label', type: 'pdf' },
    'bilingual-labels': { text: 'Back Labels', type: 'pdf' },
    'carton-labels': { text: 'EU-UCC PDF Modifier - Item', type: 'pdf' },
    'expense-allocation-invoices': { text: 'Expense Allocation Invoices', type: 'pdf' },
    'home-goods-pdf': { text: 'Home Goods PDF', type: 'pdf' },
    'pdf-manipulation': { text: 'PDF Manipulation', type: 'pdf' },
    'pdf-reports': { text: 'PDF Reports', type: 'pdf' },
    'pdf-to-excel-ucc-labels': { text: 'PDF to Excel (UCC Labels)', type: 'pdf' },
    'pallet-labels': { text: "WAL4000 PDF's", type: 'pdf' },
    'pdq-full-pallet-pull-label': { text: 'PDQ Full Pallet Pull Label', type: 'pdf' },
    'cad-invoices': { text: 'CAD Invoices', type: 'pdf' },
    'master-retail-rugs': { text: 'Master Retail Rugs', type: 'pdf' },
    'label-printing': { text: 'Label Printing', type: 'pdf' },
    // Excel Tools
    'items-detail': { text: 'General Imports', type: 'excel' },
    // 'design-items-detail': { text: 'Design Items Detail', type: 'excel' }, // DISABLED - Interface hidden
    // 'eu-items-detail': { text: 'EU Items Detail', type: 'excel' }, // DISABLED - Interface hidden
    'excel-export-item-images': { text: 'Excel Export Item Images', type: 'excel' },
    // 'general-imports' is served by 'items-detail' route above
    // 'inactive-items': { text: 'Inactive Items', type: 'excel' }, // DISABLED - Interface hidden
    // 'item-image-grouping': { text: 'Item Image Grouping', type: 'excel' }, // DISABLED - Interface hidden
    'po-distribution': { text: 'PO Distribution', type: 'excel' },
    'po-distribution-whs': { text: 'PO Distribution WHS', type: 'excel' },
    // 'product-building': { text: 'Product Building', type: 'excel' }, // DISABLED - Interface hidden
    // 'saf1909-inventory-analysis': { text: 'SAF1909 Inventory Analysis', type: 'excel' }, // DISABLED - Interface hidden
    'sales-orders-export': { text: 'Sales Orders Export', type: 'excel' },
    // 'unpublished-items': { text: 'Unpublished Items', type: 'excel' }, // DISABLED - Interface hidden
    // 'us-items-detail': { text: 'US Items Detail', type: 'excel' }, // DISABLED - Interface hidden
    'excel-reports': { text: 'Excel Reports', type: 'excel' },
    'canadian-13-provinces-freight': { text: 'Canadian 13 Provinces Freight', type: 'excel' },
    'eu-freight': { text: 'EU Freight', type: 'excel' },
    // Link Tools
    'aws-file-links': { text: 'AWS File Links', type: 'link' },
    // 'aws-image-links-jeff': { text: 'AWS Image Links Jeff', type: 'link' },
    // 'jeff-furniture': { text: 'Jeff Furniture', type: 'link' },
    'aws-file-links-alternate': { text: 'AWS File Links Alternate', type: 'link' },
    'collection-image-links': { text: 'Collection Image Links', type: 'link' },
    'wayfair-asset-upload': { text: 'Wayfair Asset Upload', type: 'link' },
    'amazon-bulk-image-uploader': { text: 'Amazon Bulk Image Uploader', type: 'link' },
    'broken-link-checker': { text: 'Broken Link Checker', type: 'link' },
    'bulk-image-downloader': { text: 'Bulk Image Downloader', type: 'link' },
    'image-checker': { text: 'Image Checker', type: 'link' },
    // Other Tools
    'file-converter': { text: 'File Converter', type: 'other' },
    'pallet-optimizer': { text: 'Pallet Optimizer', type: 'other' },
    'asset-explorer': { text: 'Asset Explorer', type: 'other' },
    '48-state-freight': { text: '48 State Freight', type: 'other' },
    'customer-style-search': { text: 'Customer Style Search', type: 'other' },
    'document-translation': { text: 'Document Translation', type: 'other' },
    // 'ftp-transfers': { text: 'FTP Transfers', type: 'other' }, // DISABLED - Interface hidden
    // 'furniture-regulations': { text: 'Furniture Regulations', type: 'other' }, // DISABLED - Interface hidden
    // 'furniture-testing': { text: 'Furniture Testing', type: 'other' }, // DISABLED - Interface hidden
    'marketing-copies': { text: 'Marketing Copies', type: 'other' },
    // 'pallet-configuration': { text: 'Pallet Configuration', type: 'other' }, // DISABLED - Interface hidden
    'price-scraper': { text: 'Price Scraper', type: 'other' },
    'reports-scheduler': { text: 'Reports Scheduler', type: 'other' },
    'product-details': { text: 'Product Details', type: 'other' },
    'remove-background': { text: 'Remove Background', type: 'other' },
    'sales-dashboard': { text: 'Sales Dashboard', type: 'reports' },
    'social-media-content': { text: 'Social Media Content', type: 'other' },
    'product-data-validator': { text: 'Product Data Validator', type: 'other' },
    'email-campaign-builder': { text: 'Email Campaign Builder', type: 'other' },
    'barcode-generator': { text: 'Barcode / QR Generator', type: 'other' },
    // 'ftp-transfer': { text: 'FTP Transfer', type: 'other' }, // DISABLED - Interface hidden
    // Landing Pages
    '': { text: 'Dashboard Home', type: 'other' },
    'pdf-tools': { text: 'PDF Tools', type: 'pdf' },
    'excel-tools': { text: 'Excel Tools', type: 'excel' },
    'link-tools': { text: 'Link Management', type: 'link' },
    'other-tools': { text: 'Other Tools', type: 'other' },
    // Reports
    'analytics-tools': { text: 'Analytics', type: 'reports' },
    'sales-dashboard': { text: 'Sales Dashboard', type: 'reports' },
    'sales-dashboard-v2': { text: 'Sales Dashboard V2', type: 'reports' },
    'returns-dashboard': { text: 'Returns & Quality', type: 'reports' },
    'retail-os': { text: 'RetailOS', type: 'reports' },
    'new-items': { text: 'New Items', type: 'reports' },
    'customer-style-gaps': { text: 'Customer Style Gaps', type: 'reports' },
    'collection-launches': { text: 'Collection Launches', type: 'reports' },
    'sales-pulse': { text: 'Sales Pulse', type: 'reports' },
    'reorder-console': { text: 'Reorder Console', type: 'reports' },
    // AI Tools
    'ai-tools': { text: 'AI Tools', type: 'ai' },
    'ai-image-generator': { text: 'AI Image Generator', type: 'ai' },
    'ai-video-generator': { text: 'AI Video Generator', type: 'ai' },
    'ai-image-search': { text: 'AI Image Search', type: 'ai' },
    'rug-room-shots': { text: 'Rug Room Shots', type: 'ai' },
    'seo-content-writer': { text: 'SEO Content Writer', type: 'ai' },
    'amazon-rug-titles': { text: 'Amazon Rug Titles', type: 'ai' },
    'view-updates': { text: "What's New", type: 'other' },
    // Admin Tools
    'users': { text: 'User Management', type: 'admin' },
    'roles': { text: 'Role Management', type: 'admin' },
    'notifications': { text: 'Send Notifications', type: 'admin' },
    'file-history': { text: 'File History', type: 'admin' },
  }), []);

  const trackPageVisit = (pathname) => {
    // Extract the path slug from full pathname (e.g., /dashboard/pdf-tools -> pdf-tools)
    const pathParts = pathname.split('/');
    const slug = pathParts[pathParts.length - 1] || '';

    // Skip if it's just the dashboard root without a specific interface
    if (pathname === '/dashboard' || pathname === '/dashboard/') {
      // Still track dashboard home
      const interfaceInfo = pathToInterfaceMap[''] || { text: 'Dashboard Home', type: 'other' };
      const visit = {
        path: pathname,
        text: interfaceInfo.text,
        type: interfaceInfo.type,
        timestamp: new Date().toISOString(),
      };

      try {
        const stored = localStorage.getItem(RECENT_VISITS_KEY);
        let visits = stored ? JSON.parse(stored) : [];
        // Remove duplicate if exists
        visits = visits.filter(v => v.path !== pathname);
        // Add new visit at the beginning
        visits.unshift(visit);
        // Keep only the most recent visits
        visits = visits.slice(0, MAX_RECENT_VISITS);
        localStorage.setItem(RECENT_VISITS_KEY, JSON.stringify(visits));
      } catch (err) {
        console.error('Error tracking page visit:', err);
      }
      return;
    }

    const interfaceInfo = pathToInterfaceMap[slug];
    if (!interfaceInfo) return; // Don't track unknown paths

    const visit = {
      path: pathname,
      text: interfaceInfo.text,
      type: interfaceInfo.type,
      timestamp: new Date().toISOString(),
    };

    try {
      const stored = localStorage.getItem(RECENT_VISITS_KEY);
      let visits = stored ? JSON.parse(stored) : [];
      // Remove duplicate if exists
      visits = visits.filter(v => v.path !== pathname);
      // Add new visit at the beginning
      visits.unshift(visit);
      // Keep only the most recent visits
      visits = visits.slice(0, MAX_RECENT_VISITS);
      localStorage.setItem(RECENT_VISITS_KEY, JSON.stringify(visits));
    } catch (err) {
      console.error('Error tracking page visit:', err);
    }
  };

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, left: 0 });   // UX polish: start each tool at the top
    trackPageVisit(location.pathname);
    // Update browser tab title based on current page
    const slug = location.pathname.split('/').pop() || '';
    const info = pathToInterfaceMap[slug];
    document.title = info ? `${info.text} - Theezdocs` : 'Theezdocs';
    const t = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  /* -------- Chat Widget State -------- */
  // Make chat storage key user-specific to prevent chat history from persisting across accounts
  const getChatStorageKey = () => {
    const currentUserId = localStorage.getItem('userId') || 'anonymous';
    return `ezprocessing_help_center_chat_${currentUserId}`;
  };
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on initial render (user-specific)
    try {
      const saved = localStorage.getItem(getChatStorageKey());
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHeight, setChatHeight] = useState(400);
  const [chatWidth, setChatWidth] = useState(300);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Save messages to localStorage whenever they change (user-specific)
  useEffect(() => {
    try {
      localStorage.setItem(getChatStorageKey(), JSON.stringify(messages));
    } catch (err) {
      console.error('Failed to save chat messages:', err);
    }
  }, [messages]);

  // Reload chat messages when user changes (handles login/logout scenarios)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(getChatStorageKey());
      setMessages(saved ? JSON.parse(saved) : []);
    } catch {
      setMessages([]);
    }
  }, [userId]);

  // Clear chat history function
  const clearChatHistory = () => {
    setMessages([]);
    localStorage.removeItem(getChatStorageKey());
  };

  // Handle chat scroll to detect if not at bottom
  const handleChatScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollToBottom(!isAtBottom);
  };

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    const container = document.getElementById('chat-messages-container');
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const startResizeY = (e) => {
    e.preventDefault();
    const isTouch = e.type === 'touchstart';
    const startY = isTouch ? e.touches[0].clientY : e.clientY;
    const startHeight = chatHeight;
    const doResize = (moveEvent) => {
      if (moveEvent.type === 'touchmove') moveEvent.preventDefault();
      const currentY = isTouch ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const deltaY = startY - currentY;
      let newHeight = startHeight + deltaY;
      newHeight = Math.max(200, Math.min(newHeight, window.innerHeight * 0.8));
      setChatHeight(newHeight);
    };
    const stopResize = () => {
      window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', doResize);
      window.removeEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
    };
    window.addEventListener(isTouch ? 'touchmove' : 'mousemove', doResize, { passive: false });
    window.addEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
  };

  const startResizeX = (e) => {
    e.preventDefault();
    const isTouch = e.type === 'touchstart';
    const startX = isTouch ? e.touches[0].clientX : e.clientX;
    const startWidth = chatWidth;
    const doResize = (moveEvent) => {
      if (moveEvent.type === 'touchmove') moveEvent.preventDefault();
      const currentX = isTouch ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const deltaX = startX - currentX;
      let newWidth = startWidth + deltaX;
      newWidth = Math.max(200, Math.min(newWidth, window.innerWidth * 0.8));
      setChatWidth(newWidth);
    };
    const stopResize = () => {
      window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', doResize);
      window.removeEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
    };
    window.addEventListener(isTouch ? 'touchmove' : 'mousemove', doResize, { passive: false });
    window.addEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
  };

  const startResizeCorner = (e) => {
    e.preventDefault();
    const isTouch = e.type === 'touchstart';
    const startX = isTouch ? e.touches[0].clientX : e.clientX;
    const startY = isTouch ? e.touches[0].clientY : e.clientY;
    const startWidth = chatWidth;
    const startHeight = chatHeight;
    const doResize = (moveEvent) => {
      if (moveEvent.type === 'touchmove') moveEvent.preventDefault();
      const currentX = isTouch ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const currentY = isTouch ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const deltaX = startX - currentX;
      const deltaY = startY - currentY;
      let newWidth = startWidth + deltaX;
      let newHeight = startHeight + deltaY;
      newWidth = Math.max(200, Math.min(newWidth, window.innerWidth * 0.8));
      newHeight = Math.max(200, Math.min(newHeight, window.innerHeight * 0.8));
      setChatWidth(newWidth);
      setChatHeight(newHeight);
    };
    const stopResize = () => {
      window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', doResize);
      window.removeEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
    };
    window.addEventListener(isTouch ? 'touchmove' : 'mousemove', doResize, { passive: false });
    window.addEventListener(isTouch ? 'touchend' : 'mouseup', stopResize);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    const userMessage = { role: 'user', content: inputMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setChatLoading(true);
    // Scroll to bottom after sending message
    setTimeout(scrollToBottom, 100);
    try {
      const response = await fetch(`${API_BASE_URL}/api/Chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
        },
        body: JSON.stringify({ message: inputMessage }),
      });
      if (!response.ok) throw new Error(`Failed to send message: ${response.status}`);
      const data = await response.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
      // Scroll to bottom after receiving reply
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
      setTimeout(scrollToBottom, 100);
    } finally {
      setChatLoading(false);
    }
  };

  // Process context - kept for potential future use
  // const { processes: runningProcesses, cancelProcess } = useProcess();

  // Helper function to filter items based on permissions
  const filterItemsByPermission = (items) => {
    // Admins and owners see everything
    if (isAdmin || isOwner) {
      console.log('[RBAC] User is admin/owner, showing all items');
      return items;
    }
    // Filter items based on user's permitted paths
    const filtered = items.filter(item => hasAccessToPath(item.path));
    console.log('[RBAC] Filtering items:', items.length, '-> ', filtered.length, 'isAdmin:', isAdmin, 'isOwner:', isOwner);
    return filtered;
  };

  /* -------- dropdown items -------- */
  const allDropdownItems = [
    {
      icon: FaFilePdf,
      items: [
        { text: 'AMA9999 UCC Labels', path: 'ama9999-ucc-labels' },
        { text: 'Amazon DIO UCC Label', path: 'amazon-di-ucc-label' },
        { text: 'Back Labels', path: 'bilingual-labels' },
        { text: 'EU-UCC PDF Modifier - Item', path: 'carton-labels' },
        { text: 'Expense Allocation Invoices', path: 'expense-allocation-invoices' },
        { text: 'Home Goods PDF', path: 'home-goods-pdf' },
        { text: 'PDF Manipulation', path: 'pdf-manipulation' },
        { text: 'PDF Reports', path: 'pdf-reports' },
        { text: 'PDF to Excel (UCC Labels)', path: 'pdf-to-excel-ucc-labels' },
        { text: "WAL4000 PDF's", path: 'pallet-labels' },
        { text: 'PDQ Full Pallet Pull Label', path: 'pdq-full-pallet-pull-label' },
        { text: 'CAD Invoices', path: 'cad-invoices' },
        { text: 'Master Retail Rugs', path: 'master-retail-rugs' },
        { text: 'Label Printing', path: 'label-printing' },
      ],
    },
    {
      icon: RiFileExcel2Fill,
      items: [
        // { text: 'Design Items Detail', path: 'design-items-detail' }, // DISABLED - Interface hidden
        // { text: 'EU Items Detail', path: 'eu-items-detail' }, // DISABLED - Interface hidden
        { text: 'Excel Export Item Images', path: 'excel-export-item-images' },
        { text: 'General Imports', path: 'items-detail' },
        // { text: 'Inactive Items', path: 'inactive-items' }, // DISABLED - Interface hidden
        // { text: 'Item Image Grouping', path: 'item-image-grouping' }, // DISABLED - Interface hidden
        { text: 'PO Distribution', path: 'po-distribution' },
        { text: 'PO Distribution WHS', path: 'po-distribution-whs' },
        // { text: 'Product Building', path: 'product-building' }, // DISABLED - Interface hidden
        // { text: 'SAF1909 Inventory Analysis', path: 'saf1909-inventory-analysis' }, // DISABLED - Interface hidden
        { text: 'Sales Orders Export', path: 'sales-orders-export' },
        // { text: 'Unpublished Items', path: 'unpublished-items' }, // DISABLED - Interface hidden
        // { text: 'US Items Detail', path: 'us-items-detail' }, // DISABLED - Interface hidden
        { text: 'Excel Reports', path: 'excel-reports' },
        { text: 'Canadian 13 Provinces Freight', path: 'canadian-13-provinces-freight' },
        { text: 'EU Freight', path: 'eu-freight' },
      ],
    },
    {
      icon: IoLinkSharp,
      items: [
        { text: 'AWS File Links', path: 'aws-file-links' },
        // { text: 'AWS Image Links Jeff', path: 'aws-image-links-jeff' },
        // { text: 'Jeff Furniture', path: 'jeff-furniture' },
        { text: 'AWS File Links Alternate', path: 'aws-file-links-alternate' },
        { text: 'Collection Image Links', path: 'collection-image-links' },
        { text: 'Wayfair Asset Upload', path: 'wayfair-asset-upload' },
        { text: 'Amazon Bulk Image Uploader', path: 'amazon-bulk-image-uploader' },
        { text: 'Broken Link Checker', path: 'broken-link-checker' },
        { text: 'Asset Explorer', path: 'asset-explorer' },
        { text: 'Bulk Image Downloader', path: 'bulk-image-downloader' },
      ],
    },
    {
      icon: FaBrain,
      items: [
        { text: 'AI Image Generator', path: 'ai-image-generator' },
        { text: 'AI Video Generator', path: 'ai-video-generator' },
        { text: 'AI Image Search', path: 'ai-image-search' },
        { text: 'Rug Room Shots', path: 'rug-room-shots' },
        { text: 'SEO Content Writer', path: 'seo-content-writer' },
        { text: 'Amazon Rug Titles', path: 'amazon-rug-titles' },
      ],
    },
    {
      icon: FaChartBar,
      items: [
        { text: 'Sales Dashboard', path: 'sales-dashboard' },
        { text: 'Sales Dashboard V2', path: 'sales-dashboard-v2' },
        { text: 'Returns & Quality', path: 'returns-dashboard' },
        { text: 'RetailOS', path: 'retail-os' },
        { text: 'New Items', path: 'new-items' },
        { text: 'Customer Style Gaps', path: 'customer-style-gaps' },
        { text: 'Collection Launches', path: 'collection-launches' },
        { text: 'Sales Pulse', path: 'sales-pulse' },
        { text: 'Reorder Console', path: 'reorder-console' },
      ],
    },
  ];

  const allOthersDropdown = {
    icon: IoApps,
    items: [
      { text: '48 State Freight', path: '48-state-freight' },
      { text: 'Customer Style Search', path: 'customer-style-search' },
      { text: 'Document Translation', path: 'document-translation' },
      // { text: 'FTP Transfers', path: 'ftp-transfers' }, // DISABLED - Interface hidden
      // { text: 'Furniture Regulations', path: 'furniture-regulations' }, // DISABLED - Interface hidden
      // { text: 'Furniture Testing', path: 'furniture-testing' }, // DISABLED - Interface hidden
      { text: 'Marketing Copies', path: 'marketing-copies' },
      // { text: 'Pallet Configuration', path: 'pallet-configuration' }, // DISABLED - Interface hidden
      { text: 'Price Scraper', path: 'price-scraper' },
      { text: 'Product Details', path: 'product-details' },
      { text: 'Remove Background', path: 'remove-background' },
      { text: 'Social Media Content', path: 'social-media-content' },
      { text: 'Product Data Validator', path: 'product-data-validator' },
      { text: 'Email Campaign Builder', path: 'email-campaign-builder' },
      { text: 'Barcode / QR Generator', path: 'barcode-generator' },
      { text: 'File Converter', path: 'file-converter' },
      { text: 'Pallet Optimizer', path: 'pallet-optimizer' },
      { text: 'Reports Scheduler', path: 'reports-scheduler' },
      // { text: 'FTP Transfer', path: 'ftp-transfer' }, // DISABLED - Interface hidden
    ],
  };

  // Filter dropdown items based on user permissions
  const dropdownItems = allDropdownItems.map(dropdown => ({
    ...dropdown,
    items: filterItemsByPermission(dropdown.items)
  })).filter(dropdown => dropdown.items.length > 0);

  const othersDropdown = {
    ...allOthersDropdown,
    items: filterItemsByPermission(allOthersDropdown.items)
  };

  // Determine which category landing pages are accessible (user has at least one interface in that category)
  const accessibleCategoryPaths = new Set(['/dashboard']); // Dashboard home always accessible
  if (dropdownItems.some(d => d.icon === FaFilePdf)) accessibleCategoryPaths.add('/dashboard/pdf-tools');
  if (dropdownItems.some(d => d.icon === RiFileExcel2Fill)) accessibleCategoryPaths.add('/dashboard/excel-tools');
  if (dropdownItems.some(d => d.icon === IoLinkSharp)) accessibleCategoryPaths.add('/dashboard/link-tools');
  if (dropdownItems.some(d => d.icon === FaBrain)) accessibleCategoryPaths.add('/dashboard/ai-tools');
  if (dropdownItems.some(d => d.icon === FaChartBar)) accessibleCategoryPaths.add('/dashboard/analytics-tools');
  if (othersDropdown.items.length > 0) accessibleCategoryPaths.add('/dashboard/other-tools');

  // Admin dropdown (only visible to admins/owners)
  const adminDropdown = {
    icon: AdminPanelSettingsIcon,
    items: [
      { text: 'User Management', path: 'admin/users' },
      { text: 'Role Management', path: 'admin/roles' },
      { text: 'File History', path: 'admin/file-history' },
      { text: 'Send Notifications', path: 'admin/notifications' },
    ],
  };

  /* -------- keyboard shortcuts for dropdowns -------- */
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [linksDropdownOpen, setLinksDropdownOpen] = useState(false);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [othersDropdownOpen, setOthersDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isTyping = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';
      const isDropdownOpen = pdfDropdownOpen || excelDropdownOpen || linksDropdownOpen || aiDropdownOpen || reportsDropdownOpen || othersDropdownOpen;

      if (isTyping) return;

      // Cmd+K / Ctrl+K → open the command palette. Intercept before
      // letting other Ctrl-prefixed shortcuts pass through to the browser.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCommandPaletteOpen(true);
        return;
      }
      // Let other Ctrl/Cmd shortcuts pass through (Ctrl+F, Ctrl+H, etc.)
      if (e.ctrlKey || e.metaKey) return;

      const blockedKeysWhenDropdownOpen = ['f', 'F', 'h', 'H'];
      if (isDropdownOpen && blockedKeysWhenDropdownOpen.includes(e.key)) return;

      const closeAllDropdowns = () => {
        setPdfDropdownOpen(false);
        setExcelDropdownOpen(false);
        setLinksDropdownOpen(false);
        setAiDropdownOpen(false);
        setReportsDropdownOpen(false);
        setOthersDropdownOpen(false);
      };

      switch (e.key) {
        case '1':
          e.preventDefault();
          closeAllDropdowns();
          navigate('view-updates');
          break;
        case '2':
          e.preventDefault();
          closeAllDropdowns();
          setPdfDropdownOpen(true);
          setTimeout(() => setPdfDropdownOpen(false), 5000);
          break;
        case '3':
          e.preventDefault();
          closeAllDropdowns();
          setExcelDropdownOpen(true);
          setTimeout(() => setExcelDropdownOpen(false), 5000);
          break;
        case '4':
          e.preventDefault();
          closeAllDropdowns();
          setLinksDropdownOpen(true);
          setTimeout(() => setLinksDropdownOpen(false), 5000);
          break;
        case '5':
          e.preventDefault();
          closeAllDropdowns();
          setAiDropdownOpen(true);
          setTimeout(() => setAiDropdownOpen(false), 5000);
          break;
        case '6':
          e.preventDefault();
          closeAllDropdowns();
          setReportsDropdownOpen(true);
          setTimeout(() => setReportsDropdownOpen(false), 5000);
          break;
        case '7':
          e.preventDefault();
          closeAllDropdowns();
          setOthersDropdownOpen(true);
          setTimeout(() => setOthersDropdownOpen(false), 5000);
          break;
        case 'h':
        case 'H':
          e.preventDefault();
          setHistoryOpen((prev) => !prev);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          setDrawerOpen((prev) => !prev);
          break;
        case '/':
          e.preventDefault();
          setSearchOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
          break;
        case '?':
          e.preventDefault();
          setShortcutsOpen(true);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, pdfDropdownOpen, excelDropdownOpen, linksDropdownOpen, aiDropdownOpen, reportsDropdownOpen, othersDropdownOpen]);

  /* ——— searchable content index ——— */
  const searchIndex = {
    // Landing Pages
    'excel-tools': ['Excel Tools', 'Excel Reports', 'Data Export', 'Spreadsheet Processing', 'Bulk Operations', 'Inventory Analysis', 'Product Data Management'],
    'pdf-tools': ['PDF Tools', 'Label Generation', 'Document Creation', 'PDF Manipulation', 'Invoice Generation', 'UCC Labels'],
    'link-tools': ['Link Management', 'Image Links', 'AWS Links', 'File Distribution', 'URL Management', 'Media Content'],
    'other-tools': ['Other Tools', 'Specialized Tools', 'Business Operations', 'Freight Calculation', 'Document Translation', 'Testing'],
    'ai-tools': ['AI Tools', 'Artificial Intelligence', 'Machine Learning', 'Image Generation', 'Gemini', 'Product Images'],
    'analytics-tools': ['Reports', 'Analytics', 'Dashboards', 'Sales Data', 'Revenue'],
    
    // PDF Tools
    'pdf-manipulation': ['Combine PDF', 'Split PDF', 'Delete Pages', 'Export PDF', 'Insert Pages', 'Reorder Pages', 'Watermark PDF', 'Compress PDF', 'Pages per Split Chunk', 'Page Ranges', 'Insert at', 'Selected Files'],
    'pdf-reports': ['PDF Reports', 'Video Links', 'Assembly Instructions', 'EU Assembly', 'Flammability Reports', 'Download Links', 'AWS Links'],
    'ama9999-ucc-labels': ['AMA9999', 'UCC', 'Generate Labels', 'Barcode', 'Product Labels'],
    'amazon-di-ucc-label': ['Amazon', 'DIO', 'Direct Import', 'UCC Label', 'Compliance'],
    'bilingual-labels': ['Back Labels', 'Bilingual', 'Care Instructions', 'Product Information', 'Generate', 'Preview'],
    'carton-labels': ['EU', 'European Union', 'UCC Modifier', 'Carton Labels', 'Compliance', 'Regulations'],
    'expense-allocation-invoices': ['Expense Allocation', 'Invoices', 'Safavieh', 'Department Costs', 'Financial Reports'],
    'home-goods-pdf': ['Home Goods', 'Catalog', 'Product Documentation', 'High Quality Images'],
    'pdf-to-excel-ucc-labels': ['PDF to Excel', 'UCC Labels', 'Data Conversion', 'Bulk Editing'],
    'pallet-labels': ['WAL4000', 'Pallet Labels', 'Product Lines', 'Specifications', 'Documentation', 'Bill of Lading'],
    'pdq-full-pallet-pull-label': ['PDQ', 'Full Pallet', 'Pull Label', 'Shipping Labels', 'GTIN', 'Barcodes'],
    'cad-invoices': ['CAD', 'Canadian', 'Invoices', 'Currency', 'Financial'],
    'master-retail-rugs': ['Master Retail', 'Rug Gallery', 'Labels', 'Design ID', 'Web ID', 'Item ID', 'Price Tags', 'Retail Store'],
    'label-printing': ['Label Printing', 'UPC Labels', 'QR Code', 'Custom Rug Labels', 'SKU Labels', 'Barcode'],

    // Excel Tools
    // 'design-items-detail': ['Design Items', 'Specifications', 'Pricing', 'Availability', 'Product Details'], // DISABLED - Interface hidden
    // 'eu-items-detail': ['EU Items', 'European Market', 'Compliance', 'Regulatory Information'], // DISABLED - Interface hidden
    'excel-export-item-images': ['Export Item Images', 'Metadata', 'Catalog Management', 'Marketing'],
    'items-detail': ['General Imports', 'Data Import', 'Bulk Upload', 'Excel Import', 'CSV Import', 'File Import'],
    // 'inactive-items': ['Inactive Items', 'Inventory Management', 'Reactivation', 'Clearance'], // DISABLED - Interface hidden
    'item-image-grouping': ['Image Grouping', 'Categories', 'Collections', 'Organization'],
    'po-distribution': ['PO Distribution', 'Purchase Orders', 'Warehouses', 'Locations'],
    'po-distribution-whs': ['PO Distribution WHS', 'Warehouse Distribution', 'Purchase Orders', 'Sales History', 'Nearest Warehouse'],
    'reports-scheduler': ['Reports Scheduler', 'Scheduled Reports', 'Cron', 'Schedule', 'Report Runs', 'New Items Report', 'Customer Styles', 'SKU to Build', 'SEI1900', 'Missing Pile Content', 'Designs with Color', 'Sales Return Summary', 'Warehouse Reports', 'Automation'],
    'pallet-optimizer': ['Pallet Optimizer', 'Palletization', 'Pallet Packing', 'Shipping', 'Sales Orders', 'Bin Packing', 'Logistics'],
    // 'product-building': ['Product Building', 'ItemID', 'Excel Processing', 'Collection ID', 'With Color', 'Design Without Color', 'Components', 'Variations'], // DISABLED - Interface hidden
    'saf1909-inventory-analysis': ['SAF1909', 'Inventory Analysis', 'Compliance', 'Reporting'],
    'sales-orders-export': ['Sales Orders', 'Export Data', 'Analysis', 'Reporting', 'Filters'],
    // 'unpublished-items': ['Unpublished Items', 'Review', 'Sales Channels', 'Website Publishing'], // DISABLED - Interface hidden
    // 'us-items-detail': ['US Items', 'American Market', 'Pricing', 'Availability', 'Product Info'], // DISABLED - Interface hidden
    'excel-reports': ['Excel Reports', 'Pre-generated', 'Business Metrics', 'Inventory Data', 'Download'],
    'canadian-13-provinces-freight': ['Canadian 13 Provinces Freight', 'Canada Freight', 'Purolator', 'Purolator Ground', 'Freight', 'Shipping Cost', 'Canadian', 'Provinces', 'Providence', 'Dim Weight', 'Surcharge'],
    'eu-freight': ['EU Freight', 'Europe Freight', 'European', 'UPS', 'FedEx', 'Fed Ex', 'Freight', 'Shipping Cost', 'Parcel', 'Dim Weight', 'Girth', 'Surcharge', 'Boxed Packaging'],
    
    // Link Management Tools
    'aws-file-links': ['AWS Links', 'S3 Storage', 'File Distribution', 'Secure Links', 'Team Sharing'],
    // 'aws-image-links-jeff': ['AWS Image Links Jeff', 'Image Links', 'S3 Upload', 'Product Images', 'Jeff', 'Image Classification'],
    // 'jeff-furniture': ['Jeff Furniture', 'Furniture Image Links', 'Furniture Images', 'S3 Upload', 'BED Items', 'Jeff Furniture'],
    'aws-file-links-alternate': ['AWS File Links Alternate', 'Alternate Links', 'Rugs Furniture', 'Image Links', 'S3 Upload', 'Combined'],
    'collection-image-links': ['Collection Image Links', 'Collection', 'ROOM', 'ROOMAI', 'Rug Collection', 'Image Links'],
    'wayfair-asset-upload': ['Wayfair Asset Upload', 'Wayfair', 'Product Catalog API', 'Augusta Home', 'SPN', 'Media Upload', 'Image Upload', 'Supplier'],
    'amazon-bulk-image-uploader': ['Amazon Bulk Image Uploader', 'Bulk Upload', 'ASIN', 'Shot Type', 'Amazon Images', 'Zip', 'Rugs', 'Furniture', 'Lighting'],
    'broken-link-checker': ['Broken Link Checker', 'Broken Links', 'Dead Links', 'URL Check', 'Link Validator', '404', 'Status Check'],

    // Other Tools
    '48-state-freight': ['Watson', 'AI Freight', 'Logistics', 'Shipping Calculation', '48 States'],
    'customer-style-search': ['Customer Style Search', 'US Styles', 'Search Styles', 'Product Preferences'],
    'document-translation': ['Document Translation', 'Azure AI Translation', 'Multiple Languages', 'Text Translation', 'Document Translation', 'Excel CSV', 'Word PDF', 'Image Translation', '13 Languages'],
    // 'ftp-transfers': ['FTP Transfers', 'Bulk Data', 'Partners', 'Suppliers', 'File Exchange'], // DISABLED
    // 'furniture-regulations': ['Furniture Regulations', 'Safety Requirements', 'Compliance', 'Regional Standards'], // DISABLED
    // 'furniture-testing': ['Furniture Testing', 'Schedules', 'Results', 'Certification', 'Quality Control'], // DISABLED
    'marketing-copies': ['Marketing Copy', 'SEO Optimization', 'Brand Consistency', 'Product Descriptions'],
    // 'pallet-configuration': ['Pallet Configuration', 'Shipping Efficiency', 'Warehouse Management', 'Optimization'], // DISABLED
    'price-scraper': ['Price Scraper', 'Amazon', 'Walmart', 'Bed Bath Beyond', 'Product Prices', 'SKU Search', 'Price Comparison', 'Scraping'],
    'product-details': ['Product Details', 'Specifications', 'Attributes', 'Information Management'],
    'remove-background': ['Remove Background', 'Product Images', 'Professional Listings', 'Image Processing'],
    'sales-dashboard': ['Sales Dashboard', 'Top SKUs', 'Top Customers', 'Collections', 'Sizes', 'Colors', 'Styles', 'Warehouses', 'Zip Codes', 'Sales Analytics', 'Revenue'],
    'sales-dashboard-v2': ['Sales Dashboard V2', 'Sales Analytics', 'ClickHouse', 'Revenue', 'Units', 'Warehouses', 'Collections', 'States', 'Top SKUs', 'Customers', 'Forecast', 'Drop-ship', 'Zip Codes', 'Reports'],
    'returns-dashboard': ['Returns', 'Quality', 'Return Rate', 'Credits', 'Credit Memo', 'RMA', 'Net Revenue', 'OSR', 'Refunds', 'Top Offenders', 'Reports'],
    'retail-os': ['RetailOS', 'Retail OS', 'Promotion', 'Promotions', 'Promotion Lift', 'Promotion Calendar', 'Walmart', 'Home Depot', "Lowe's", 'Marketplace', 'DSV', 'ROAS', 'Advertising', 'Account Workspace', 'SKU Intelligence', 'Executive Briefing', 'Reports'],
    'new-items': ['New Items', 'New Arrivals', 'New Products', 'Launch', 'Listing', 'UPC', 'Catalog', 'Selling', 'Marketplace', 'ETA', 'Stock'],
    'customer-style-gaps': ['Customer Style Gaps', 'Customer Styles', 'Assortment', 'Not Built', 'Gap', 'CSL', 'Coverage', 'Available Styles', 'Build'],
    'collection-launches': ['Collection Launches', 'Launch Performance', 'New Collections', 'Ramp', 'Cohort', 'First Sale', 'Trajectory'],
    'sales-pulse': ['Sales Pulse', 'Customer Health', 'At Risk', 'Churn', 'Movers', 'Gainers', 'Decliners', 'Growing', 'Fading', 'Radar'],
    'reorder-console': ['Reorder Console', 'Reorder', 'Replenishment', 'Purchase Orders', 'PO', 'Expedite', 'Class A', 'Buyer Decisions', 'Lead Time', 'Stock Runway'],
    'social-media-content': ['Social Media', 'Instagram', 'Facebook', 'Pinterest', 'TikTok', 'Captions', 'Hashtags', 'Content Generator'],
    'product-data-validator': ['Product Data Validator', 'Feed Validation', 'Data Quality', 'UPC', 'Required Fields', 'Image Check'],
    'email-campaign-builder': ['Email Campaign', 'Email Marketing', 'Subject Lines', 'Newsletter', 'Promotional Email', 'A/B Test'],
    'barcode-generator': ['Barcode', 'QR Code', 'Code 128', 'UPC', 'EAN', 'Label', 'Scan'],
    // AI Tools
    'ai-image-generator': ['AI Image Generator', 'Gemini', 'Product Images', 'Lifestyle Images', 'Prompt Library', 'Furniture', 'Rugs', 'Artificial Intelligence'],
    'ai-video-generator': ['AI Video Generator', 'Veo', 'Video', 'Promotional Video', '4K', 'Product Video', 'Commercial', 'Cinematic'],
    'ai-image-search': ['AI Image Search', 'Semantic Search', 'Vector Search', 'Find Products', 'Room Scenes', 'Identify Product', 'Similar Images', 'Embedding', 'Visual Search'],
    'rug-room-shots': ['Rug Room Shots', 'Room Shots', 'Rug Swap', 'Room Swap', 'Base Rooms', 'Style Sorter', 'ROOMAI', 'Peter Rooms'],
    'seo-content-writer': ['SEO', 'Blog', 'Article', 'Content Writer', 'Keywords', 'Meta Description', 'Organic Traffic', 'Search Engine'],
    // 'ftp-transfer': ['FTP Transfer', 'SFTP', 'Loft Submission', 'File Download', 'AR ZIP', '2D 3D Files', 'Batch Processing'], // DISABLED
    
    // General Dashboard
    'view-updates': ['Updates', 'What\'s New', 'Release Notes', 'New Features', 'Announcements'],
    'dashboard': ['Dashboard', 'Home', 'Overview', 'Statistics', 'Quick Actions', 'Recent Activity']
  };
  // Complete interface list including landing pages
  const landingPages = [
    { text: 'Excel Tools', path: '/dashboard/excel-tools' },
    { text: 'PDF Tools', path: '/dashboard/pdf-tools' },
    { text: 'Link Management Tools', path: '/dashboard/link-tools' },
    { text: 'Other Tools', path: '/dashboard/other-tools' },
    { text: 'AI Tools', path: '/dashboard/ai-tools' },
    { text: 'Analytics', path: '/dashboard/analytics-tools' },
    { text: 'Dashboard Home', path: '/dashboard' },
  ].filter(page => accessibleCategoryPaths.has(page.path));
  
  const interfaceList = [
    ...dropdownItems.flatMap((d) => d.items), 
    ...othersDropdown.items,
    ...landingPages
  ];

  /* ——— SEARCH ——— */
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);         // UX polish #6: scroll-to-top button
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false); // UX polish #9: logout confirm

  // UX polish #6: reveal the scroll-to-top button once the user scrolls down.
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listItemRefs = useRef([]);
  const timer = useRef(null);

  const highlightText = (text, query) => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return text;
    // escape regex specials so queries like "c++" or "(new)" can't throw,
    // and use split-with-capture parity instead of a stateful /g regex test
    const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    return String(text).split(regex).map((part, i) =>
      i % 2 === 1 ? <span key={i} className="highlight">{part}</span> : part
    );
  };

  /* ——— search scoring: every term must match somewhere; best match wins ———
     120 exact · 100 prefix · 80 word-prefix · 60 substring · 30 one-typo word
     · 20 in-order subsequence (missing letters). */
  const editDistanceLeq1 = (a, b) => {
    if (a === b) return true;
    const la = a.length; const lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    let i = 0; let j = 0; let edits = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) { i += 1; j += 1; continue; }
      if (edits) return false;
      edits = 1;
      if (la > lb) i += 1;
      else if (lb > la) j += 1;
      else { i += 1; j += 1; }
    }
    return edits + (la - i) + (lb - j) <= 1;
  };
  const isSubsequence = (needle, hay) => {
    let i = 0;
    for (let j = 0; j < hay.length && i < needle.length; j += 1) {
      if (hay[j] === needle[i]) i += 1;
    }
    return i === needle.length;
  };
  const termScore = (term, text) => {
    const t = String(text || '').toLowerCase();
    if (!t) return 0;
    if (t === term) return 120;
    if (t.startsWith(term)) return 100;
    const words = t.split(/[^a-z0-9]+/).filter(Boolean);
    if (words.some((w) => w.startsWith(term))) return 80;
    if (t.includes(term)) return 60;
    if (term.length >= 4 && words.some((w) => editDistanceLeq1(w, term))) return 30;
    if (term.length >= 3 && isSubsequence(term, t)) return 20;
    return 0;
  };
  // same gate the favorites drawer applies — search must not rank tools the
  // user can't open (the route guard still blocks, but the result misleads)
  const canAccessSearchPath = (path) => {
    if (isAdmin || isOwner) return true;
    if (!path) return false;
    const abs = path.startsWith('/') ? path : `/dashboard/${path}`;
    const slug = abs === '/dashboard' ? 'dashboard' : abs.replace(/^\/dashboard\//, '');
    return hasAccessToPath(slug) || hasAccessToPath(path) || accessibleCategoryPaths.has(abs);
  };
  const RECENTS_KEY = 'ezpSearchRecents_v1';
  const readRecents = () => {
    try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]').filter((r) => r && r.path && r.label); }
    catch { return []; }
  };
  const rememberRecent = (r) => {
    if (!r.path) return;
    const next = [{ path: r.path, label: r.label }, ...readRecents().filter((x) => x.path !== r.path)].slice(0, 8);
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { /* storage full/blocked — recents are a nicety */ }
  };

  // Helper function to get appropriate icon for interface
  const getInterfaceIcon = (path) => {
    if (path.includes('admin/users') || path.includes('admin/roles') || path.includes('admin/notifications') || path.includes('admin/file-history')) return AdminPanelSettingsIcon;
    if (path.includes('ai-')) return FaBrain;
    if (path.includes('pdf') || path.includes('label')) return FaFilePdf;
    if (path.includes('excel') || path.includes('export') || path.includes('items') || path.includes('po-distribution') || path.includes('sales-orders')) return RiFileExcel2Fill;
    if (path.includes('dashboard') || path.includes('performance') || path.includes('analytics') || path.includes('report')) return MdAnalytics;
    if (path.includes('link') || path.includes('image') || path.includes('aws')) return IoLinkSharp;
    if (path.includes('dashboard')) return IoApps;
    return IoApps;
  };

  useEffect(() => {
    const h = setTimeout(() => {
      if (!query.trim()) {
        // empty query: the popper is still useful — recently opened + favorites
        if (!searchOpen) { setResults([]); setSelectedIndex(-1); return; }
        // array order MUST mirror group render order (Favorites, then Recently
        // opened) or arrow-highlight and Enter act on different rows
        const idle = [];
        const idlePaths = new Set();
        favorites.slice(0, 6).forEach((fav) => {
          if (fav.path && canAccessSearchPath(fav.path)) {
            idle.push({ type: 'favorite', label: fav.text, path: fav.path, icon: getInterfaceIcon(fav.path), subtitle: 'Favorite' });
            idlePaths.add(fav.path);
          }
        });
        readRecents().forEach((r) => {
          if (!idlePaths.has(r.path) && canAccessSearchPath(r.path)) {
            idle.push({ type: 'recent', label: r.label, path: r.path, icon: getInterfaceIcon(r.path), subtitle: 'Recently opened' });
          }
        });
        setResults(idle.slice(0, 10));
        setSelectedIndex(-1);
        return;
      }
      const terms = query.toLowerCase().split(/\s+/).filter(Boolean);

      // one candidate per destination: label + path + its keyword index, so
      // "churn" finds Sales Pulse once instead of one row per keyword
      const keywordsFor = (path) => {
        const key = path === '/dashboard' ? 'dashboard' : String(path || '').replace(/^\/dashboard\//, '');
        return searchIndex[key] || [];
      };
      const candidates = [];
      const seen = new Set();
      favorites.forEach((fav) => {
        if (!fav.path || seen.has(fav.path) || !canAccessSearchPath(fav.path)) return;
        seen.add(fav.path);
        candidates.push({ type: 'favorite', label: fav.text, path: fav.path, keywords: keywordsFor(fav.path), boost: 40 });
      });
      landingPages.forEach((page) => {
        if (seen.has(page.path)) return;
        seen.add(page.path);
        candidates.push({ type: 'landing-page', label: page.text, path: page.path, keywords: keywordsFor(page.path), boost: 25 });
      });
      interfaceList.forEach((i) => {
        if (seen.has(i.path) || landingPages.some((lp) => lp.path === i.path)) return;
        seen.add(i.path);
        candidates.push({ type: 'interface-name', label: i.text, path: i.path, keywords: keywordsFor(i.path), boost: 20 });
      });
      // routes with a search entry but no dropdown item ("What's New" lives
      // behind the bell icon) would otherwise be unfindable
      if (!seen.has('view-updates')) {
        candidates.push({ type: 'interface-name', label: "What's New", path: 'view-updates', keywords: keywordsFor('view-updates'), boost: 10 });
      }

      const scored = [];
      candidates.forEach((c) => {
        let total = 0;
        let matchedKeyword = '';
        for (const term of terms) {
          const labelScore = Math.max(termScore(term, c.label), termScore(term, c.path));
          let best = labelScore;
          for (const kw of c.keywords) {
            const ks = termScore(term, kw);
            if (ks > best) { best = ks; if (labelScore === 0) matchedKeyword = kw; }
          }
          if (best === 0) { total = 0; break; }   // every term must match somewhere
          total += best;
        }
        if (total > 0) {
          scored.push({
            type: c.type, label: c.label, path: c.path, icon: getInterfaceIcon(c.path),
            score: total + c.boost,
            subtitle: c.type === 'favorite' ? 'Favorite'
              : c.type === 'landing-page' ? 'Category'
              : matchedKeyword ? `matches \u201c${matchedKeyword}\u201d` : 'Interface',
          });
        }
      });

      // on-page text hits only once the query is specific enough
      const q = query.toLowerCase();
      const domHits = q.length >= 3
        ? Array.from(document.querySelectorAll('body *'))
          .filter((el) =>
            el.children.length === 0 &&
            el.textContent.toLowerCase().includes(q) &&
            el.textContent.trim().length > 5 &&
            el.textContent.trim().length < 100)
          .slice(0, 8)
          .map((el) => ({
            type: 'page-content', label: el.textContent.trim().slice(0, 80),
            element: el, icon: FaFileAlt, score: 0, subtitle: 'On this page',
          }))
        : [];

      // group order must mirror render order so keyboard indexes line up
      const typeOrder = { favorite: 0, recent: 1, 'landing-page': 2, 'interface-name': 3, 'page-content': 5 };
      const allResults = [...scored, ...domHits]
        .sort((a, b) => (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) || b.score - a.score || a.label.localeCompare(b.label))
        .slice(0, 40);

      setResults(allResults);
      setSelectedIndex(-1);
    }, 200);
    return () => clearTimeout(h);
  }, [query, favorites, searchOpen]);

  const handleKeyDown = (e) => {
    if (!searchOpen || results.length === 0) {
      if (e.key === 'Escape') { setQuery(''); setResults([]); setSearchOpen(false); inputRef.current?.blur(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = Math.min(prev + 1, results.length - 1);
        // scroll the row into view but keep focus in the input so typing continues
        listItemRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = Math.max(prev - 1, 0);
        listItemRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return newIndex;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = selectedIndex >= 0 ? results[selectedIndex] : results[0];
      if (target) handleResultClick(target);
    } else if (e.key === 'Escape') {
      setQuery('');
      setResults([]);
      setSearchOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleResultClick = (r) => {
    if (r.type === 'page-content' && r.element) {
      r.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      r.element.style.outline = '2px solid #ff4081';
      setTimeout(() => (r.element.style.outline = ''), 2000);
    } else if (r.path) {
      rememberRecent(r);
      navigate(r.path);
    }
    setResults([]);
    setSelectedIndex(-1);
    setQuery('');
    setSearchOpen(false);
  };

  const groupedResults = useMemo(() => {
    const groups = {
      'favorite': { label: 'Favorites', items: [], color: 'gold' },
      'recent': { label: 'Recently opened', items: [], color: 'text.secondary' },
      'landing-page': { label: 'Tool Categories', items: [], color: 'primary.main' },
      'interface-name': { label: 'Interfaces', items: [], color: 'text.primary' },
      'page-content': { label: 'On this page', items: [], color: 'text.disabled' },
    };
    results.forEach((r) => {
      if (groups[r.type]) {
        groups[r.type].items.push(r);
      }
    });
    return Object.values(groups).filter((g) => g.items.length > 0);
  }, [results]);

/* -------- logout helper -------- */
const handleLogout = () => {
  const savedFavorites = localStorage.getItem(FAVORITES_KEY);
  // Clear all auth tokens and session data
  localStorage.removeItem('authToken');
  localStorage.removeItem('userSession');
  localStorage.removeItem('jwtToken');
  localStorage.setItem('isLoggedIn', 'false');
  // Note: We do NOT clear per-user 2FA verification (is2FAVerified_<email>)
  // so users don't have to re-authenticate when switching accounts
  // Clear RBAC permissions
  clearPermissions();
  if (savedFavorites) localStorage.setItem(FAVORITES_KEY, savedFavorites);
  window.location.href = '/'; // Full page reload to clear all state
};

  /* ————————————————  UI  ———————————————— */
  return (
    <Box sx={{ display: 'flex' }}>
      {/* ---------- Favorites Drawer ---------- */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: isMobile ? 'auto' : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            top: isMobile ? 0 : 64,
            boxSizing: 'border-box',
            bgcolor: isDarkMode ? '#1e1f22' : '#fafafa',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Favorites</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' } }}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        <Box sx={{
          overflowY: 'auto',
          p: 2,
          pb: 4,
          height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 64px - 60px)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: isDarkMode ? '#2c2d30' : '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDarkMode ? '#4a4b4f' : '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? '#5a5b5f' : '#a8a8a8',
            },
          },
          '&::-webkit-scrollbar-thumb:active': {
            backgroundColor: isDarkMode ? '#6a6b6f' : '#8a8a8a',
          },
        }}>
          {(() => {
            // Filter favorites by permission - only show items the user has access to
            const permittedFavorites = (isAdmin || isOwner) ? favorites : favorites.filter(fav => {
              const slug = fav.path?.replace('/dashboard/', '').replace(/^\//, '') || '';
              return hasAccessToPath(slug) || accessibleCategoryPaths.has(fav.path);
            });
            return permittedFavorites.length === 0 ? (
            <Typography color="text.secondary">No favorites yet…</Typography>
          ) : (
            <List>
              {permittedFavorites.map((item, index) => {
                const getIcon = () => {
                  // First check if category is stored
                  if (item.category) {
                    switch(item.category) {
                      case 'pdf':
                        return FaFilePdf;
                      case 'excel':
                        return RiFileExcel2Fill;
                      case 'powerbi':
                        return MdAnalytics;
                      case 'links':
                        return IoLinkSharp;
                      case 'ai':
                        return FaBrain;
                      case 'reports':
                        return FaChartBar;
                      case 'admin':
                        return AdminPanelSettingsIcon;
                      case 'others':
                        return IoApps;
                      default:
                        break;
                    }
                  }
                  // Check for admin paths
                  if (item.path && (item.path.includes('admin/users') || item.path.includes('admin/roles') || item.path.includes('admin/notifications') || item.path.includes('admin/file-history'))) {
                    return AdminPanelSettingsIcon;
                  }
                  // Fallback to old method
                  for (const group of [...dropdownItems, othersDropdown]) {
                    const found = group.items.find((i) => i.path === item.path);
                    if (found) return group.icon;
                  }
                  return IoApps;
                };
                const IconComponent = getIcon();
                // Normalize paths for comparison (remove trailing slashes and compare case-insensitively)
                const normalizePath = (p) => p?.toLowerCase().replace(/\/+$/, '') || '';
                const isActive = normalizePath(location.pathname) === normalizePath(item.path);
                return (
                  <ListItem
                    key={item.path}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    button
                    onClick={() => { navigate(item.path); if (isMobile) setDrawerOpen(false); }}
                    sx={(theme) => ({
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 2,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderLeft: isActive ? '3px solid' : '3px solid transparent',
                      borderLeftColor: isActive ? theme.palette.primary.main : 'transparent',
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.18)
                        : 'transparent',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.12),
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 12px rgba(0,0,0,0.3)'
                          : '0 2px 8px rgba(0,0,0,0.1)',
                      },
                      '&:active': {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.getContrastText(theme.palette.primary.main),
                        transition: 'background-color 0.5s, color 0.5s',
                        transform: 'translateY(0px)',
                      },
                    })}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          const category = item.path.toLowerCase().includes('admin/users') || item.path.toLowerCase().includes('admin/roles') || item.path.toLowerCase().includes('admin/notifications') || item.path.toLowerCase().includes('admin/file-history') ? 'admin' :
                                      item.path.toLowerCase().includes('pdf') || item.path.toLowerCase().includes('ucc') || item.path.toLowerCase().includes('label') ? 'pdf' :
                                      item.path.toLowerCase().includes('excel') || item.path.toLowerCase().includes('xlsx') || item.path.toLowerCase().includes('report') ? 'excel' :
                                      item.path.toLowerCase().includes('link') ? 'links' : 'others';
                          toggleFavorite({ text: item.text, path: item.path, category });
                        }}
                        sx={{ color: 'gold' }}
                      >
                        ★
                      </IconButton>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
                      <Box sx={{ minWidth: 26, ml: -1, display: 'flex', alignItems: 'center' }}>
                        <IconComponent size={22} />
                      </Box>
                      <ListItemText primary={item.text} />
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          );
          })()}
        </Box>
      </Drawer>

      {/* ---------- History Drawer ---------- */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        sx={{
          width: isMobile ? 'auto' : historyDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: historyDrawerWidth,
            top: isMobile ? 0 : 64,
            boxSizing: 'border-box',
            bgcolor: isDarkMode ? '#1e1f22' : '#fafafa',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'flex-start', px: 1 }}>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}>
            <ChevronLeftIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        </Toolbar>
        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 128px)' }}>
          {/* Fixed Header Section */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                File History
              </Typography>
              <Tooltip title="Save as Excel">
                <HistoryButton
                  onClick={handleExportExcel}
                  aria-label="Save history as Excel"
                  disabled={filteredHistory.length === 0}
                >
                  <FaDownload size={20} />
                </HistoryButton>
              </Tooltip>
            </Box>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search date, email, or file"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setHistorySearch('')}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 6,
                background: isDarkMode ? '#2c2d30' : '#fff',
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">
                <IoMdSearch size={20} />
              </InputAdornment>,
              endAdornment: historySearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setHistorySearch('')}
                    aria-label="Clear search"
                  >
                    <IoMdClose size={20} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': 'Search history' }}
          />
          <Box display="flex" gap={1} mb={2} justifyContent="flex-start">
            <Tooltip title="Sort by date">
              <HistoryButton
                active={historySort === 'createdAt'}
                onClick={() => {
                  setHistorySort('createdAt');
                  setIsReverseSort((prev) => !prev);
                }}
                aria-label="Sort by date"
              >
                <FaCalendarAlt size={20} />
                {historySort === 'createdAt' && (isReverseSort ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />)}
              </HistoryButton>
            </Tooltip>
            <Tooltip title="Sort by interface">
              <HistoryButton
                active={historySort === 'interfaceName'}
                onClick={() => {
                  setHistorySort('interfaceName');
                  setIsReverseSort((prev) => !prev);
                }}
                aria-label="Sort by interface"
              >
                <FaLayerGroup size={20} />
                {historySort === 'interfaceName' && (isReverseSort ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />)}
              </HistoryButton>
            </Tooltip>
            <Tooltip title="Sort by filename">
              <HistoryButton
                active={historySort === 'filename'}
                onClick={() => {
                  setHistorySort('filename');
                  setIsReverseSort((prev) => !prev);
                }}
                aria-label="Sort by filename"
              >
                <FaFileAlt size={20} />
                {historySort === 'filename' && (isReverseSort ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />)}
              </HistoryButton>
            </Tooltip>
            <Tooltip title="Show all history">
              <HistoryButton
                active={historyFilter === 'all'}
                onClick={() => setHistoryFilter('all')}
                aria-label="Show all history"
              >
                <IoApps size={20} />
              </HistoryButton>
            </Tooltip>
            <Tooltip title="Show downloads only">
              <HistoryButton
                active={historyFilter === 'download'}
                onClick={() => setHistoryFilter('download')}
                aria-label="Show downloads"
              >
                <IoMdCloudDownload size={20} color={isDarkMode ? '#37474f' : '#e0e0e0'} />
              </HistoryButton>
            </Tooltip>
            <Tooltip title="Show uploads only">
              <HistoryButton
                active={historyFilter === 'upload'}
                onClick={() => setHistoryFilter('upload')}
                aria-label="Show uploads"
              >
                <IoMdCloudUpload size={20} color={isDarkMode ? '#006064' : '#b2ebf2'} />
              </HistoryButton>
            </Tooltip>
          </Box>
          </Box>
          
          {/* Scrollable History Items Section */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 2, 
            pt: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: isDarkMode ? '#2c2d30' : '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDarkMode ? '#4a4b4f' : '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: isDarkMode ? '#5a5b5f' : '#a8a8a8',
              },
            },
            '&::-webkit-scrollbar-thumb:active': {
              backgroundColor: isDarkMode ? '#6a6b6f' : '#8a8a8a',
            },
          }}>
            {historyLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredHistory.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No history found
              </Typography>
            ) : (
              <List>
              {filteredHistory.map((h, idx) => {
                const isDownload = h.upload === true;
                let baseColor, hoverColor;
                if (muiTheme.palette.mode === 'dark') {
                  baseColor = isDownload ? '#37474f' : '#006064';
                  hoverColor = isDownload ? '#78909c' : '#4dd0e1';
                } else {
                  baseColor = isDownload ? '#e0e0e0' : '#b2ebf2';
                  hoverColor = isDownload ? '#b0bec5' : '#00acc1';
                }
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      mb: 0.5,
                      borderRadius: 6,
                      backgroundColor: baseColor,
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: hoverColor,
                        color: '#fff',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={async () => {
                        if (h.link && h.link.startsWith('batch:')) {
                          const batchId = h.link.replace('batch:', '');
                          try {
                            const token = localStorage.getItem('jwtToken');
                            const response = await fetch(`${API_BASE_URL}/api/AIImageGenerator/history/${batchId}/download`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (!response.ok) throw new Error('Download failed');
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `ai_images_${batchId.substring(0, 8)}.zip`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error('Batch download failed:', err);
                          }
                        } else {
                          window.open(h.link, '_blank');
                        }
                      }}
                      sx={{ p: 1.5 }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                          {h.userEmailId}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                          {h.filename}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {formatDateTime(h.createdAt)}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Paper>
                );
              })}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* ---------- Top Navigation Bar ---------- */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'background.default',
          color: 'text.primary',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, md: 2 }, minHeight: { xs: 56, md: 64 } }}>
          {/* LEFT: star toggle, SAFAVIEH, What's New, dropdowns */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 4 } }}>
            {/* Mobile hamburger menu */}
            {isMobile && (
              <IconButton onClick={() => setMobileNavOpen(!mobileNavOpen)} sx={{ p: 1 }}>
                <MenuIcon />
              </IconButton>
            )}

            {!isMobile && (
              <Tooltip title={drawerOpen ? 'Hide Favorites' : 'Show Favorites'}>
                <IconButton
                  size="large"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  sx={{
                    p: 1,
                    mr: 5,
                    color: drawerOpen
                      ? 'gold'
                      : (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                    '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                  }}
                >
                  <IoStarSharp size={22} />
                </IconButton>
              </Tooltip>
            )}

            <Typography
              variant="h6"
              fontWeight="bold"
              letterSpacing={1}
              onClick={() => navigate('/dashboard')}
              sx={{
                cursor: 'pointer',
                fontSize: { xs: '1rem', md: '1.25rem' },
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease',
                }
              }}
            >
              SAFAVIEH
            </Typography>

            {!isMobile && (
              <Tooltip title="What's New">
                <IconButton
                  onClick={() => navigate('view-updates')}
                  sx={{
                    color: (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                    '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                  }}
                >
                  <ImNewspaper size={22} />
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && dropdownItems.map(({ icon, items }) => {
              const isOpenMap = { [FaFilePdf]: pdfDropdownOpen, [RiFileExcel2Fill]: excelDropdownOpen, [IoLinkSharp]: linksDropdownOpen, [FaBrain]: aiDropdownOpen, [FaChartBar]: reportsDropdownOpen };
              const setIsOpenMap = { [FaFilePdf]: setPdfDropdownOpen, [RiFileExcel2Fill]: setExcelDropdownOpen, [IoLinkSharp]: setLinksDropdownOpen, [FaBrain]: setAiDropdownOpen, [FaChartBar]: setReportsDropdownOpen };
              const pathMap = { [FaFilePdf]: '/dashboard/pdf-tools', [RiFileExcel2Fill]: '/dashboard/excel-tools', [IoLinkSharp]: '/dashboard/link-tools', [FaBrain]: '/dashboard/ai-tools', [FaChartBar]: '/dashboard/analytics-tools' };
              const labelMap = { [FaFilePdf]: 'PDF', [RiFileExcel2Fill]: 'Excel', [IoLinkSharp]: 'Links', [FaBrain]: 'AI', [FaChartBar]: 'Analytics' };
              const catMap = { [FaFilePdf]: 'pdf', [RiFileExcel2Fill]: 'excel', [IoLinkSharp]: 'links', [FaBrain]: 'ai', [FaChartBar]: 'reports' };
              return (
                <Dropdown
                  key={items[0].path}
                  icon={icon}
                  items={items}
                  navigate={navigate}
                  favorites={favorites}
                  toggleFavorite={toggleFavorite}
                  isFavorite={isFavorite}
                  isOpen={isOpenMap[icon] || false}
                  setIsOpen={setIsOpenMap[icon]}
                  onIconClick={() => navigate(pathMap[icon])}
                  iconTooltip={`${labelMap[icon]} Tools - Click to view all`}
                  category={catMap[icon]}
                />
              );
            })}

            {!isMobile && othersDropdown.items.length > 0 && (
              <Dropdown
                key="others"
                icon={othersDropdown.icon}
                items={othersDropdown.items}
                navigate={navigate}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                isOpen={othersDropdownOpen}
                setIsOpen={setOthersDropdownOpen}
                onIconClick={() => navigate('/dashboard/other-tools')}
                iconTooltip="Other Tools - Click to view all"
                category="others"
              />
            )}

            {/* Admin Dropdown - Only visible to admins/owners */}
            {!isMobile && (isAdmin || isOwner) && (
              <Dropdown
                key="admin"
                icon={adminDropdown.icon}
                items={adminDropdown.items}
                navigate={navigate}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                isOpen={adminDropdownOpen}
                setIsOpen={setAdminDropdownOpen}
                onIconClick={() => navigate('/dashboard/admin/users')}
                iconTooltip="Admin - User & Role Management"
                category="admin"
              />
            )}
          </Box>

          {/* RIGHT: Search / Notifications / History / Theme / Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            {/* ------- SEARCH ------- */}
            <Box
              ref={anchorRef}
              sx={{ position: 'relative' }}
              onMouseEnter={() => {
                if (!isMobile) {
                  clearTimeout(timer.current);
                  setSearchOpen(true);
                  // focus so the user can type immediately — but never yank
                  // focus away from another input/textarea they're typing in
                  const el = document.activeElement;
                  const typingElsewhere = el && el !== inputRef.current &&
                    (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');
                  if (!typingElsewhere) setTimeout(() => inputRef.current?.focus(), 80);
                }
              }}
              onMouseLeave={() => {
                // hover-close only while the panel is untouched: hover-open
                // grants the input focus, so "focused input + empty query" still
                // counts as untouched; a typed query or focus on a result row
                // (keyboard nav) keeps it open. Any keystroke/click inside the
                // panel cancels this timer.
                const active = document.activeElement;
                const onResultRow = anchorRef.current?.contains(active) && active !== inputRef.current;
                if (!isMobile && !query.trim() && !onResultRow) {
                  timer.current = setTimeout(() => setSearchOpen(false), 600);
                }
              }}
            >
              <Tooltip title="Search">
                <IconButton
                  size="large"
                  onClick={() => {
                    // On mobile, MUI temporary Drawers wrap themselves in a
                    // Modal with a focus-trap. If the hamburger / favorites /
                    // history drawer is open while Search is triggered, the
                    // trap prevents the search TextField from taking focus, so
                    // the mobile keyboard never pops up. Close any open drawer
                    // first so focus is free.
                    if (isMobile) {
                      if (mobileNavOpen) setMobileNavOpen(false);
                      if (drawerOpen) setDrawerOpen(false);
                      if (historyOpen) setHistoryOpen(false);
                    }
                    // Desktop: hover already opened the panel, so a toggle would
                    // CLOSE it on the very click meant to start searching — the
                    // click is the "commit" gesture: always open + focus.
                    // Mobile: keep the toggle, and rely on the TextField's
                    // autoFocus for the keyboard — iOS Safari only opens the
                    // on-screen keyboard when focus happens within the
                    // user-gesture frame, and a setTimeout callback is outside it.
                    if (isMobile) {
                      setSearchOpen(!searchOpen);
                    } else {
                      setSearchOpen(true);
                      setTimeout(() => inputRef.current?.focus(), 100);
                    }
                  }}
                  sx={{
                    p: 1,
                    color: (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                    '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                    ...(searchOpen && {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }),
                  }}
                >
                  <IoMdSearch size={22} />
                </IconButton>
              </Tooltip>
              <Popper
                open={searchOpen}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
                disablePortal
                modifiers={[
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    options: { altAxis: true, boundariesElement: 'viewport' },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }}>
                    <Box
                      sx={{ pt: 1 }}
                      onMouseEnter={() => { if (!isMobile) clearTimeout(timer.current); }}
                    >
                      <ClickAwayListener
                        onClickAway={() => {
                          setResults([]);
                          setQuery('');
                          setSearchOpen(false);
                        }}
                      >
                      <SearchPaper sx={{ width: { xs: 'calc(100vw - 24px)', sm: 440 } }}>
                        {/* input row, integrated into the panel */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25 }}>
                          <Box component="span" sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>
                            <IoMdSearch size={20} />
                          </Box>
                          <TextField
                            inputRef={inputRef}
                            autoFocus={isMobile}
                            fullWidth
                            variant="standard"
                            placeholder={isMobile ? 'Search tools…' : 'Search tools, features or content…'}
                            value={query}
                            onChange={(e) => { clearTimeout(timer.current); setQuery(e.target.value); }}
                            onKeyDown={(e) => { clearTimeout(timer.current); handleKeyDown(e); }}
                            onClick={(e) => { clearTimeout(timer.current); e.stopPropagation(); }}
                            InputProps={{ disableUnderline: true, sx: { fontSize: 15 } }}
                            inputProps={{ 'aria-label': 'Search tools, interfaces, features, or content' }}
                          />
                          {query ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setQuery('');
                                inputRef.current?.focus();
                              }}
                              aria-label="Clear search"
                            >
                              <IoMdClose size={18} />
                            </IconButton>
                          ) : (
                            !isMobile && <Kbd>/</Kbd>
                          )}
                        </Box>
                        <Divider />
                        <SearchResultsArea>
                          <List dense disablePadding ref={listRef}>
                            {groupedResults.length === 0 && (
                              <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
                                <Box sx={{ color: 'text.disabled', mb: 1 }}><IoMdSearch size={28} /></Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {query.trim() ? `No matches for \u201c${query.trim()}\u201d` : 'Search every tool'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {query.trim()
                                    ? 'Try fewer or shorter words — typos are okay'
                                    : 'Your favorites and recently opened pages will appear here'}
                                </Typography>
                              </Box>
                            )}
                            {groupedResults.map((group, groupIndex) => (
                              <Box key={group.label}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 2.25,
                                    pt: groupIndex === 0 ? 1 : 1.5,
                                    pb: 0.5,
                                    fontWeight: 700,
                                    fontSize: 10.5,
                                    letterSpacing: 0.7,
                                    textTransform: 'uppercase',
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                  }}
                                >
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: group.color || 'text.disabled', flexShrink: 0 }} />
                                  {group.label}
                                  <Box component="span" sx={{ fontWeight: 500, textTransform: 'none' }}>· {group.items.length}</Box>
                                </Typography>
                                {group.items.map((r, i) => {
                                  const globalIndex = groupedResults
                                    .slice(0, groupIndex)
                                    .reduce((sum, g) => sum + g.items.length, 0) + i;
                                  const isFav = r.path && isFavorite(r.path);
                                  return (
                                    <ResultItem
                                      key={globalIndex}
                                      selected={globalIndex === selectedIndex}
                                      ref={(el) => (listItemRefs.current[globalIndex] = el)}
                                      onClick={() => handleResultClick(r)}
                                      onMouseMove={() => setSelectedIndex(globalIndex)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleResultClick(r); }
                                      }}
                                      tabIndex={0}
                                      role="option"
                                      aria-selected={globalIndex === selectedIndex}
                                      aria-label={`${r.label} (${group.label})`}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                                        <Box
                                          sx={{
                                            width: 30, height: 30, borderRadius: 1.25, flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'),
                                            color: 'primary.main',
                                          }}
                                        >
                                          <r.icon size={16} />
                                        </Box>
                                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                          <HighlightedText variant="body1" noWrap sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                            {highlightText(r.label, query)}
                                          </HighlightedText>
                                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                            {r.subtitle || (r.type === 'interface-name' ? 'Interface' : r.type === 'landing-page' ? 'Category' : 'Page text')}
                                          </Typography>
                                        </Box>
                                        {globalIndex === selectedIndex && !isMobile && <Kbd>↵</Kbd>}
                                        {r.path && (
                                          <Tooltip
                                            title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const category = r.path.toLowerCase().includes('admin/users') || r.path.toLowerCase().includes('admin/roles') || r.path.toLowerCase().includes('admin/notifications') || r.path.toLowerCase().includes('admin/file-history') ? 'admin' :
                                                            r.path.toLowerCase().includes('pdf') || r.path.toLowerCase().includes('ucc') || r.path.toLowerCase().includes('label') ? 'pdf' :
                                                            r.path.toLowerCase().includes('excel') || r.path.toLowerCase().includes('xlsx') || r.path.toLowerCase().includes('report') ? 'excel' :
                                                            r.path.toLowerCase().includes('link') ? 'links' : 'others';
                                                toggleFavorite({ text: r.label, path: r.path, category });
                                              }}
                                              className="search-row-star"
                                              sx={{
                                                color: isFav ? '#E2A400' : 'text.disabled',
                                                '&:hover': { color: '#E2A400' },
                                                ...(isFav && { opacity: '1 !important' }),
                                              }}
                                              aria-label={
                                                isFav
                                                  ? `Remove ${r.label} from favorites`
                                                  : `Add ${r.label} to favorites`
                                              }
                                            >
                                              {isFav ? '★' : '☆'}
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </ResultItem>
                                  );
                                })}
                              </Box>
                            ))}
                          </List>
                        </SearchResultsArea>
                        <Divider />
                        {/* footer: keyboard hints + live count */}
                        <Box
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            px: 1.75, py: 0.75,
                            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                          }}
                        >
                          {!isMobile ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Kbd>↑↓</Kbd>
                                <Typography variant="caption" color="text.secondary">navigate</Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Kbd>↵</Kbd>
                                <Typography variant="caption" color="text.secondary">open</Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Kbd>esc</Kbd>
                                <Typography variant="caption" color="text.secondary">close</Typography>
                              </Stack>
                            </Stack>
                          ) : <span />}
                          <Typography variant="caption" color="text.secondary">
                            {query.trim()
                              ? `${results.length} result${results.length === 1 ? '' : 's'}`
                              : results.length > 0 ? 'Quick access' : ''}
                          </Typography>
                        </Box>
                      </SearchPaper>
                      </ClickAwayListener>
                    </Box>
                  </Grow>
                )}
              </Popper>
            </Box>

            {/* ------- NOTIFICATIONS ------- */}
            <NotificationDropdown userId={userId} />

            <Tooltip title="History">
              <IconButton
                onClick={() => setHistoryOpen(!historyOpen)}
                sx={{
                  color: (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>

            <AccentColorHoverWrapper>
              <Tooltip title="Toggle Theme">
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    color: (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                    '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
            </AccentColorHoverWrapper>

            <Tooltip title="Logout">
              <IconButton
                onClick={() => setLogoutConfirmOpen(true)}
                sx={{
                  color: (t) => (t.palette.mode === 'dark' ? t.palette.grey[200] : t.palette.text.primary),
                  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
        {/* UX polish #7: thin progress bar at the bottom of the AppBar during page navigation */}
        {loading && <LinearProgress sx={{ height: 2 }} />}
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{ sx: { width: 260, pt: 2 } }}
      >
        <Box sx={{ px: 2, pb: 2, borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="bold" letterSpacing={1}>SAFAVIEH</Typography>
        </Box>
        <List>
          <ListItem button onClick={() => { setDrawerOpen(!drawerOpen); setMobileNavOpen(false); }}>
            <ListItemIcon><IoStarSharp size={20} color={drawerOpen ? 'gold' : undefined} /></ListItemIcon>
            <ListItemText primary="Favorites" />
          </ListItem>
          <ListItem button onClick={() => { navigate('view-updates'); setMobileNavOpen(false); }}>
            <ListItemIcon><ImNewspaper size={20} /></ListItemIcon>
            <ListItemText primary="What's New" />
          </ListItem>
          <ListItem button onClick={() => { setHistoryOpen(!historyOpen); setMobileNavOpen(false); }}>
            <ListItemIcon><HistoryIcon /></ListItemIcon>
            <ListItemText primary="History" />
          </ListItem>
          <Divider sx={{ my: 1 }} />
          <ListItem button onClick={() => { navigate('/dashboard/pdf-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><FaFilePdf size={20} /></ListItemIcon>
            <ListItemText primary="PDF Tools" />
          </ListItem>
          <ListItem button onClick={() => { navigate('/dashboard/excel-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><RiFileExcel2Fill size={20} /></ListItemIcon>
            <ListItemText primary="Excel Tools" />
          </ListItem>
          <ListItem button onClick={() => { navigate('/dashboard/link-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><IoLinkSharp size={20} /></ListItemIcon>
            <ListItemText primary="Link Tools" />
          </ListItem>
          <ListItem button onClick={() => { navigate('/dashboard/other-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><IoApps size={20} /></ListItemIcon>
            <ListItemText primary="Other Tools" />
          </ListItem>
          <ListItem button onClick={() => { navigate('/dashboard/ai-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><FaBrain size={20} /></ListItemIcon>
            <ListItemText primary="AI Tools" />
          </ListItem>
          <ListItem button onClick={() => { navigate('/dashboard/analytics-tools'); setMobileNavOpen(false); }}>
            <ListItemIcon><FaChartBar size={20} /></ListItemIcon>
            <ListItemText primary="Reports Tools" />
          </ListItem>
          {(isAdmin || isOwner) && (
            <>
              <Divider sx={{ my: 1 }} />
              {adminDropdown.items.map((item) => (
                <ListItem key={item.path} button onClick={() => { navigate(`/dashboard/${item.path}`); setMobileNavOpen(false); }}>
                  <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </>
          )}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 7,
          ml: isMobile ? 0 : (drawerOpen ? -37.5 : -68.5),
          mr: isMobile ? 0 : (historyOpen ? `${historyDrawerWidth}px` : 0),
          pl: 0,
          pr: { xs: 0, md: 3 },
          // RetailOS takes the viewport below the bar as a fixed panel; main has
          // nothing in flow there, so it must not add height the body could scroll.
          pb: immersive ? 0 : 4,
          transition: 'margin-left 0.25s ease, padding 0.25s ease',
          bgcolor: isDarkMode ? '#1c1d1f' : '#ecf0f1',
          color: 'text.primary',
          minHeight: immersive ? 0 : 'calc(100vh - 56px)',
          height: 'auto',
          position: 'relative',
          overflowX: 'hidden',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <>
            {/* Back button — steps back one entry in the in-app history (navigate(-1)) */}
            {!immersive && (
              <>
                <Box sx={{ px: 3, pt: 1 }}>
                  <Tooltip title="Back">
                    <IconButton size="small" onClick={() => navigate(-1)}><ArrowBackIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
                <Breadcrumb />
              </>
            )}
            <Outlet context={{ setChatOpen, setSearchOpen, setShortcutsOpen, setCommandPaletteOpen }} />
          </>
        )}

        {/* UX polish #6: scroll-to-top button (hidden while the chat panel is open to avoid overlap) */}
        {showScrollTop && !chatOpen && (
          <Tooltip title="Back to top">
            <IconButton
              aria-label="Scroll back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                position: 'fixed',
                bottom: 84,
                right: isMobile ? 16 : (historyOpen ? historyDrawerWidth + 20 : 20),
                zIndex: 1300,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 3,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Help Chat Widget */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: isMobile ? 16 : (historyOpen ? historyDrawerWidth + 20 : 20),
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          {/* Chat Panel */}
          {chatOpen && (
            <Paper
              elevation={0}
              sx={{
                width: chatWidth,
                height: chatHeight,
                maxHeight: '80vh',
                minHeight: 300,
                minWidth: 320,
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxShadow: isDarkMode
                  ? '0 4px 24px rgba(0,0,0,0.5)'
                  : '0 4px 24px rgba(0,0,0,0.15)',
                border: `1px solid ${isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[200]}`,
                animation: 'slideUp 0.2s ease-out',
                '@keyframes slideUp': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  bgcolor: isDarkMode ? 'primary.dark' : 'primary.main',
                  color: 'white',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 24 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      Help Center
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                      Ask about tools & features
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {messages.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={clearChatHistory}
                      title="Clear chat history"
                      sx={{
                        color: 'white',
                        opacity: 0.7,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', opacity: 1 },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setChatOpen(false)}
                    sx={{
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                    }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Resize Handles */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  cursor: 'ns-resize',
                  zIndex: 10,
                }}
                onMouseDown={startResizeY}
                onTouchStart={startResizeY}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 5,
                  cursor: 'ew-resize',
                  zIndex: 10,
                }}
                onMouseDown={startResizeX}
                onTouchStart={startResizeX}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 12,
                  height: 12,
                  cursor: 'nwse-resize',
                  zIndex: 11,
                }}
                onMouseDown={startResizeCorner}
                onTouchStart={startResizeCorner}
              />

              {/* Messages Area */}
              <Box
                id="chat-messages-container"
                onScroll={handleChatScroll}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  position: 'relative',
                  bgcolor: isDarkMode ? muiTheme.palette.background.default : muiTheme.palette.grey[50],
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: isDarkMode ? muiTheme.palette.grey[700] : muiTheme.palette.grey[300],
                    borderRadius: 3,
                  },
                }}
              >
                {messages.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 2, px: 1.5 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Welcome to Help Center
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Ask any question about EZProcessing tools
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {[
                        'How do I merge PDFs?',
                        'How do I generate marketing copy?',
                        'How do I use AI sales analysis?',
                        'How do I export product data?',
                        'How do I convert files?',
                        'What tools do we have?',
                      ].map((q) => (
                        <Box key={q} onClick={() => { setInputMessage(q); }} sx={{
                          px: 1, py: 0.5, borderRadius: 2, fontSize: '0.7rem', cursor: 'pointer',
                          border: `1px solid ${isDarkMode ? muiTheme.palette.grey[700] : muiTheme.palette.grey[300]}`,
                          bgcolor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[100],
                          color: 'text.secondary', transition: 'all 0.15s',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: isDarkMode ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)' },
                        }}>
                          {q}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {messages.map((msg, index) => {
                  // Tool path detection for assistant messages
                  const toolPaths = {
                    'PDF Manipulation': 'pdf-manipulation', 'AMA9999 UCC Labels': 'ama9999-ucc-labels',
                    'Amazon DI UCC Label': 'amazon-di-ucc-label', 'Back Labels': 'bilingual-labels',
                    'EU-UCC PDF Modifier': 'carton-labels', 'Expense Allocation': 'expense-allocation-invoices',
                    'Home Goods PDF': 'home-goods-pdf', 'PDF Reports': 'pdf-reports',
                    'PDF to Excel': 'pdf-to-excel-ucc-labels', 'WAL4000': 'pallet-labels',
                    'PDQ Full Pallet': 'pdq-full-pallet-pull-label', 'CAD Invoices': 'cad-invoices',
                    'Master Retail Rugs': 'master-retail-rugs', 'Label Printing': 'label-printing',
                    'General Imports': 'items-detail', 'Items Detail': 'items-detail',
                    'Excel Export Item Images': 'excel-export-item-images', 'Export Images': 'excel-export-item-images',
                    'PO Distribution': 'po-distribution', 'PO Distribution WHS': 'po-distribution-whs', 'Pallet Optimizer': 'pallet-optimizer', 'Reports Scheduler': 'reports-scheduler', 'Sales Orders Export': 'sales-orders-export',
                    'Excel Reports': 'excel-reports', 'Canadian 13 Provinces Freight': 'canadian-13-provinces-freight',
                    'Canadian Freight': 'canadian-13-provinces-freight', 'EU Freight': 'eu-freight',
                    'Europe Freight': 'eu-freight', 'AWS File Links': 'aws-file-links',
                    'Amazon Bulk Image': 'amazon-bulk-image-uploader', 'Broken Link Checker': 'broken-link-checker',
                    'Asset Explorer': 'asset-explorer', 'Bulk Image Downloader': 'bulk-image-downloader',
                    'File Converter': 'file-converter', 'AI Image Generator': 'ai-image-generator',
                    'AI Video Generator': 'ai-video-generator', 'Amazon Rug Titles': 'amazon-rug-titles',
                    'SEO Content Writer': 'seo-content-writer', 'Marketing Cop': 'marketing-copies',
                    'Email Campaign': 'email-campaign-builder', 'Sales Dashboard': 'sales-dashboard', 'Sales Dashboard V2': 'sales-dashboard-v2', 'Returns & Quality': 'returns-dashboard', 'RetailOS': 'retail-os', 'Retail OS': 'retail-os', 'New Items': 'new-items', 'Customer Style Gaps': 'customer-style-gaps', 'Collection Launches': 'collection-launches', 'Sales Pulse': 'sales-pulse',
                    '48 State Freight': '48-state-freight', 'Barcode': 'barcode-generator',
                    'QR Generator': 'barcode-generator', 'Customer Style': 'customer-style-search',
                    'Document Translator': 'document-translation', 'Price Scraper': 'price-scraper',
                    'Product Data Validator': 'product-data-validator', 'Product Details': 'product-details',
                    'Remove Background': 'remove-background', 'Social Media': 'social-media-content',
                    'Inactive Items': 'inactive-items',
                  };
                  // Find tool mentions in assistant messages
                  const mentionedTools = msg.role === 'assistant'
                    ? Object.entries(toolPaths).filter(([name]) => msg.content?.includes(name)).slice(0, 3)
                    : [];

                  return (
                  <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <Typography variant="caption" sx={{ mb: 0.5, px: 1, color: 'text.secondary', fontSize: '0.65rem' }}>
                      {msg.role === 'user' ? 'You' : 'Help Center'}
                    </Typography>
                    <Box sx={{
                      maxWidth: '85%', px: 1.5, py: 1,
                      borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                      bgcolor: msg.role === 'user' ? (isDarkMode ? 'primary.dark' : 'primary.main') : (isDarkMode ? muiTheme.palette.grey[800] : 'white'),
                      color: msg.role === 'user' ? 'white' : muiTheme.palette.text.primary,
                      boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                      wordBreak: 'break-word',
                    }}>
                      <Typography variant="body2" sx={{
                        lineHeight: 1.6, whiteSpace: 'pre-wrap', fontSize: '0.85rem',
                        '& a': { color: msg.role === 'user' ? 'inherit' : 'primary.main', textDecoration: 'underline' },
                      }}>
                        {msg.content}
                      </Typography>
                      {/* Go-to-tool buttons for mentioned tools */}
                      {mentionedTools.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, pt: 0.75, borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
                          {mentionedTools.map(([name, path]) => (
                            <Box key={path} onClick={() => { navigate(`/dashboard/${path}`); setChatOpen(false); }}
                              sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: 1, py: 0.3, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                                bgcolor: isDarkMode ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)',
                                color: 'primary.main', border: `1px solid ${isDarkMode ? 'rgba(25,118,210,0.25)' : 'rgba(25,118,210,0.2)'}`,
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: isDarkMode ? 'rgba(25,118,210,0.2)' : 'rgba(25,118,210,0.15)', transform: 'translateY(-1px)' },
                              }}>
                              Open {name.length > 20 ? name.substring(0, 20) + '...' : name} →
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  );
                })}
                {chatLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.5, px: 1, color: 'text.secondary', fontSize: '0.65rem' }}
                    >
                      Help Center
                    </Typography>
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: '12px 12px 12px 2px',
                        bgcolor: isDarkMode ? muiTheme.palette.grey[800] : 'white',
                        boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          animation: 'typing 1.4s infinite ease-in-out both',
                          animationDelay: '0s',
                        }}
                      />
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          animation: 'typing 1.4s infinite ease-in-out both',
                          animationDelay: '0.2s',
                        }}
                      />
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          animation: 'typing 1.4s infinite ease-in-out both',
                          animationDelay: '0.4s',
                        }}
                      />
                      <style>{`
                        @keyframes typing {
                          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
                          40% { transform: scale(1); opacity: 1; }
                        }
                      `}</style>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Scroll to Bottom Button */}
              {showScrollToBottom && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 80,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                  }}
                >
                  <IconButton
                    onClick={scrollToBottom}
                    size="small"
                    sx={{
                      bgcolor: isDarkMode ? muiTheme.palette.grey[800] : 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: `1px solid ${muiTheme.palette.divider}`,
                      '&:hover': {
                        bgcolor: isDarkMode ? muiTheme.palette.grey[700] : muiTheme.palette.grey[100],
                      },
                    }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              )}

              {/* Input Area */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: isDarkMode ? muiTheme.palette.background.paper : 'white',
                  borderTop: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    bgcolor: isDarkMode ? muiTheme.palette.grey[900] : muiTheme.palette.grey[100],
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                    border: `1px solid ${isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[300]}`,
                    transition: 'border-color 0.2s ease',
                    '&:focus-within': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type your question..."
                    multiline
                    maxRows={4}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: '0.875rem',
                        py: 0.5,
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || chatLoading}
                    size="small"
                    sx={{
                      bgcolor: inputMessage.trim() && !chatLoading ? 'primary.main' : 'transparent',
                      color: inputMessage.trim() && !chatLoading ? 'white' : 'text.disabled',
                      width: 32,
                      height: 32,
                      mb: 0.25,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: inputMessage.trim() && !chatLoading ? 'primary.dark' : 'transparent',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    <FaArrowUp size={12} />
                  </IconButton>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    color: 'text.disabled',
                    fontSize: '0.65rem',
                  }}
                >
                  Press Enter to send, Shift+Enter for new line
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Floating Action Button */}
          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              width: 56,
              height: 56,
              bgcolor: isDarkMode ? 'primary.dark' : 'primary.main',
              color: 'white',
              boxShadow: isDarkMode
                ? '0 3px 12px rgba(0,0,0,0.4)'
                : '0 3px 12px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: isDarkMode ? 'primary.main' : 'primary.dark',
                transform: 'scale(1.05)',
              },
            }}
          >
            {chatOpen ? <IoMdClose size={24} /> : <ChatBubbleOutlineIcon sx={{ fontSize: 26 }} />}
          </IconButton>
        </Box>

        {/* Global Cmd/Ctrl+K command palette. */}
        <CommandPalette
          open={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          actions={[
            { id: 'cmd-toggle-theme', label: 'Toggle dark / light mode', category: 'Settings', hint: 'Switch theme', run: toggleTheme },
            { id: 'cmd-toggle-favorites', label: 'Toggle Favorites sidebar', category: 'Navigation', hint: 'F', run: () => setDrawerOpen(p => !p) },
            { id: 'cmd-toggle-history', label: 'Toggle History sidebar', category: 'Navigation', hint: 'H', run: () => setHistoryOpen(p => !p) },
            { id: 'cmd-shortcuts', label: 'Show keyboard shortcuts', category: 'Help', hint: '?', run: () => setShortcutsOpen(true) },
            { id: 'cmd-search', label: 'Open global search', category: 'Navigation', hint: '/', run: () => { setSearchOpen(true); setTimeout(() => inputRef.current?.focus(), 120); } },
            { id: 'cmd-chat', label: 'Open AI chat', category: 'Help', hint: 'Ask the assistant', run: () => setChatOpen(true) },
            { id: 'cmd-whats-new', label: 'View What\'s New', category: 'Navigation', hint: 'New features & interfaces', run: () => navigate('/dashboard/view-updates') },
          ]}
        />

        {/* UX polish #9: confirm before logging out (prevents losing in-progress work) */}
        <Dialog
          open={logoutConfirmOpen}
          onClose={() => setLogoutConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Log out?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You'll be returned to the sign-in screen. Any unsaved work on the current page may be lost.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setLogoutConfirmOpen(false)} color="inherit">Cancel</Button>
            <Button
              onClick={() => { setLogoutConfirmOpen(false); handleLogout(); }}
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
            >
              Log out
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global keyboard-shortcut help dialog. Triggered from anywhere
            by pressing `?`, or by code via the outlet-context
            setShortcutsOpen prop on a child route. */}
        <Dialog
          open={shortcutsOpen}
          onClose={() => setShortcutsOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <KeyboardIcon color="primary" />
              <span>Keyboard Shortcuts</span>
            </Box>
            <IconButton size="small" onClick={() => setShortcutsOpen(false)}>
              <IoMdClose size={18} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <List dense>
              {[
                { keys: '/', action: 'Open the global search' },
                { keys: '?', action: 'Show this dialog' },
                { keys: '1', action: 'Announcements' },
                { keys: '2', action: 'PDF Tools' },
                { keys: '3', action: 'Excel Tools' },
                { keys: '4', action: 'Link Management' },
                { keys: '5', action: 'AI Tools' },
                { keys: '6', action: 'Analytics' },
                { keys: '7', action: 'Other Tools' },
                { keys: 'F', action: 'Toggle Favorites sidebar' },
                { keys: 'H', action: 'Toggle History sidebar' },
                { keys: 'Esc', action: 'Close dialogs / drawers' },
              ].map((s, idx, arr) => (
                <React.Fragment key={s.keys + idx}>
                  <ListItem sx={{ px: 0 }}>
                    <Chip
                      label={s.keys}
                      size="small"
                      sx={{
                        fontWeight: 700, fontFamily: 'monospace',
                        minWidth: 36, height: 24, mr: 1.5,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        bgcolor: isDarkMode ? 'grey.800' : 'grey.100',
                      }}
                    />
                    <ListItemText primary={s.action} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  {idx < arr.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
        </Dialog>

      </Box>
    </Box>
  );
};

export default Dashboard;